// Osaka, "Lantern night on Dotonbori" (a July festival night). Seen from the north-bank Tombori River Walk a little
// west of Ebisubashi, looking east along the canal and south across it: the bridge packed with people on the left,
// the oval Ferris wheel beyond it on the north bank, and across the water the south bank's wall of giant signs, led by
// an invented neon octopus. Paper lanterns line both river walks, yellow tour boats slide under the bridge, a
// takoyaki stall steams on the near boardwalk, and the black canal doubles all of it.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, rect, makeHalo, drawHalo, reflect, streak, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const STREET = 132;                                   // far-bank street level: the bridge deck at its south end
const FWALK = 160;                                    // the far river walk (feet stand on row 159)
const WATER = 166;                                    // waterline at the far quay
const RAIL = 198;                                     // top of the near rail
const BOARD = 208;                                    // the near boardwalk begins
const deckY = x => 120 + Math.round(x * 0.06);        // bridge deck row at column x (the near end is higher)
const BR = { x1: 204, ab: 150 };                      // the bridge's south end and where its abutment starts
const VP = { x: 58, y: 157 };                         // where the canal vanishes under the bridge
const WHEEL = { cx: 34, cy: 92, a: 13, b: 50, n: 10 };   // the oval Ferris wheel on the north bank
const HERO = { x: 209, y: 26, w: 54, h: 102 };        // the giant octopus board's face
const BB = { x: 152, y: 56, w: 50, h: 20 };           // the billboard's face, on the corner building beyond the bridge
const STALL = { x: 392, top: 192, counter: 225, feet: 238 };
const LANE = 194;                                     // waterline of the boats crossing in front of us
const ACCENT = '#ff1fe6';
// The share of the crowd the scene shows by default: 1 = everyone, 0.5 = about half, just above 0 = only the cast the
// scene's story needs (cooks and stall keepers, the busker, boat crews, the people a timed event is about), and exactly
// 0 = nobody at all. Tune it per city; render(t, { crowd }) takes any other share for one frame.
const CROWD = 0.5;

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const mixc = (a, b, t) => { const A = typeof a === 'string' ? C(a) : a, B = typeof b === 'string' ? C(b) : b; return [0, 1, 2].map(k => Math.round(lerp(A[k], B[k], t))); };
const sh = (col, k) => mixc(col, k < 1 ? '#05040c' : '#ffffff', k < 1 ? 1 - k : k - 1);   // darker (k < 1) or lighter (k > 1)
const css = c => typeof c === 'string' ? c : `rgb(${c[0]},${c[1]},${c[2]})`;
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  const P = {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col, a) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) P.set(k, y, col, a); },
    line(x0, y0, x1, y1, col, th = 1) {
      const n = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
      for (let s = 0; s <= n; s++) { const X = lerp(x0, x1, s / n), Y = lerp(y0, y1, s / n); if (th > 1) P.fill(Math.round(X), Math.round(Y), th, th, col); else P.set(X, Y, col); }
    },
    /** filled ellipse; col is a colour or (i, j, dx, dy) => colour (dx, dy in -1..1) or null to skip a pixel */
    ell(cx, cy, rx, ry, col) {
      for (let j = Math.floor(cy - ry); j <= Math.ceil(cy + ry); j++) for (let i = Math.floor(cx - rx); i <= Math.ceil(cx + rx); i++) {
        const dx = (i - cx) / (rx + 0.35), dy = (j - cy) / (ry + 0.35);
        if (dx * dx + dy * dy > 1) continue;
        const v = typeof col === 'function' ? col(i, j, dx, dy) : col;
        if (v) P.set(i, j, v);
      }
    },
    /** FONT3 text at scale sc; returns the width */
    txt(s, x0, y0, col, sc = 1, gap = 1) {
      let cx = x0;
      for (const ch of s) {
        const g = FONT3[ch];
        if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') P.fill(cx + i * sc, y0 + j * sc, sc, sc, col); });
        cx += (3 + gap) * sc;
      }
      return cx - x0 - gap * sc;
    },
    /** string art: key maps a character to a colour ('.' or unknown = skip) */
    rows(g, x0, y0, key, flip = false) {
      g.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) P.set(x0 + (flip ? row.length - 1 - i : i), y0 + j, col); } });
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
  return P;
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
const flipped = c => { const [f, x] = makeCanvas(c.width, c.height); x.translate(c.width, 0); x.scale(-1, 1); x.drawImage(c, 0, 0); return f; };
/** flattened dithered ellipse of light (pools on the boards and the water) */
function makePool(rx, ry, col, maxA) {
  const p = pix(rx * 2 + 1, ry * 2 + 1), c = C(col);
  for (let y = 0; y <= ry * 2; y++) for (let x = 0; x <= rx * 2; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = a;
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
function makePuff(r, light, dark) {                   // dithered soft blob for steam
  const s = r * 2 + 1, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.set(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}

/* ================= Invented kana-like glyphs (no real words) ================= */
const G7 = [
  ['.#####.', '.....#.', '..####.', '..#....', '.######', '.....#.', '...##..'],
  ['#..#...', '#.####.', '#..#.#.', '#..#.#.', '..#..#.', '.#...#.', '#..##..'],
  ['..#....', '######.', '..#..#.', '.##..#.', '#.#.#..', '..#.#..', '..##.##'],
  ['.#.#.#.', '.#.#.#.', '.....#.', '....#..', '...#...', '..#....', '##.....'],
  ['#######', '...#...', '.#####.', '...#...', '..#.#..', '.#...#.', '#.....#'],
  ['.###...', '....#..', '#####..', '....#..', '.####.#', '#...##.', '.###..#'],
  ['#.....#', '.#...#.', '#######', '...#...', '.#####.', '...#...', '..##...'],
  ['..###..', '.#...#.', '....#..', '.####..', '....#..', '#...#..', '.###...'],
];
const G5 = [
  ['####.', '...#.', '.###.', '.#...', '.####'], ['#.#..', '#.###', '#.#.#', '..#.#', '.#.#.'], ['.#...', '####.', '.#.#.', '#..#.', '.##.#'],
  ['#.#.#', '#.#.#', '....#', '...#.', '.##..'], ['#####', '..#..', '.###.', '..#..', '.#.#.'], ['.##..', '...#.', '####.', '#..##', '.##.#'],
  ['#...#', '.###.', '..#..', '.###.', '..#..'], ['..##.', '.#..#', '..##.', '...#.', '###..'],
];
const glyphP = (p, g, x, y, col) => g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') p.set(x + i, y + j, col); });

/* ================= Sky (a humid July night, the neon haze low down) ================= */
const SKY = ['#04051a', '#060722', '#0a0a2c', '#100c38', '#180e46', '#221052', '#2e125e', '#3c1468', '#4c1670', '#5e1a78', '#72207e', '#882882', '#9e3286'];
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 150), 1.4) * 0.62;
    v += Math.max(0, 1 - Math.hypot((i - 108) / 150, (y - 128) / 96)) * 0.5;     // the canal canyon's glow
    v += Math.max(0, 1 - Math.hypot((i - 350) / 230, (y - 44) / 50)) * 0.2;      // and the sign wall's
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
const STARS = [];
{ const r = rng(12); while (STARS.length < 30) STARS.push({ x: Math.floor(r() * W), y: 1 + Math.floor(r() * 40), k: 20 + Math.floor(r() * 50), ph: r(), b: 0.3 + r() * 0.5 }); }
const MOON = { x: 174, y: 17 };
function renderMoon() {                               // a waxing gibbous moon, soft in the haze
  const p = pix(15, 15);
  p.ell(7, 7, 6, 6, (i, j, dx, dy) => {
    if (Math.hypot(i - 12.5, j - 5) < 5.2) return '#2a2058';
    return (dx + dy < -0.7) ? '#fff6dc' : hash(i, j, 3) < 0.22 ? '#d8c8a8' : '#f2e2c0';
  });
  return p.done();
}
/* ================= Clouds (tileable, lit pink from below by the signs) ================= */
const CLOUD_DEFS = [
  { seed: 17, y: -4, h: 40, banks: 4, k: 1, rx: [14, 28], ry: [3, 6],
    pal: { top: '#2c2468', body: '#1e1756', core: '#17124c', bot: '#5a2a84', bot2: '#3a1e68' } },
  { seed: 29, y: 34, h: 56, banks: 3, k: 2, rx: [10, 20], ry: [3, 6],
    pal: { top: '#7040a0', body: '#3a2262', core: '#301c58', bot: '#c850a0', bot2: '#803484' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 60 + r() * 120, base = h * (0.45 + r() * 0.3), np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.3, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.5;
      if (v > best) best = v;
    }
    D[y * W + x] = best + 0.16 * Math.sin(TAU * 11 * x / W + y * 0.5 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.3);
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    p.set(x, y, get(x, y - 1) + th <= 0.03 ? P.top : get(x, y + 1) + th <= 0.03 ? P.bot
      : get(x, y + 2) <= 0.05 + bayer(x, y) * 0.2 ? P.bot2 : v > 0.55 + th ? P.core : P.body);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Far towers down the canal, seen through the gap ================= */
function renderFar() {
  const p = pix(W, H), r = rng(23), wins = [], blinks = [];
  for (let x = 44; x < 160;) {
    const bw = 5 + Math.floor(r() * 9), top = 78 + Math.floor(r() * 30) - (x > 86 && x < 124 ? 10 : 0);
    for (let y = top; y < 130; y++) for (let i = x; i < x + bw; i++) p.set(i, y, y === top ? '#4a2a70' : i === x ? '#3a2060' : (i + y) % 5 === 0 ? '#2e1a52' : '#2a184c');
    for (let wy = top + 2; wy < 128; wy += 3) for (let wx = x + 1; wx < x + bw - 1; wx += 2)
      if (r() < 0.34) wins.push({ id: wins.length, x: wx, y: wy, w: 1, h: 1, n: 2 + Math.floor(r() * 6), p: 0.6, off: r(), col: r() < 0.6 ? '#e0a070' : '#a8a0e0' });
    if (r() < 0.4 && top < 96) { p.fill(x + (bw >> 1), top - 4, 1, 4, '#3a2060'); blinks.push([x + (bw >> 1), top - 5]); }
    x += bw;
  }
  return { c: trim(p), wins, blinks };
}

/* ================= The buildings beyond the bridge: the wheel's side (north bank) and the canyon's south side ================= */
/** a dark facade from row top to row bot with a grid of small windows; pushes the ones that toggle to wins */
function facade(p, x0, x1, top, bot, r, wins, { body = '#17132e', wx = 3, wy = 5, ww = 1, wh = 2, lit = 0.5, skip } = {}) {
  for (let y = top; y < bot; y++) for (let i = x0; i <= x1; i++)
    p.set(i, y, y === top ? sh(body, 1.9) : i === x0 ? sh(body, 1.35) : i === x1 ? sh(body, 0.65) : (i - x0) % 6 === 0 ? sh(body, 1.14) : body);
  for (let y = top + 3; y < bot - wh; y += wy) for (let i = x0 + 2; i <= x1 - 1 - ww; i += wx) {
    if (skip && skip(i, y)) continue;
    p.fill(i, y, ww, wh, sh(body, 0.55));
    if (r() < lit) wins.push({ id: wins.length, x: i, y, w: ww, h: wh, n: 3 + Math.floor(r() * 8), p: 0.62, off: r(),
      col: r() < 0.7 ? (r() < 0.5 ? '#ffd28a' : '#ffb868') : '#b8d4ff' });
  }
}
const NEON = ['#ff4fa8', '#5ee8ff', '#ffe060', '#7dff8a', '#ff6a4a', '#c070ff'];
/** a small vertical neon strip sign (static, lit) */
function strip(p, x, y, h, col, list) {
  p.fill(x - 1, y - 1, 5, h + 2, '#120c1a');
  for (let j = 0; j < h; j++) for (let i = 0; i < 3; i++) p.set(x + i, y + j, hash(x + i, y + j, 7) < 0.6 ? col : sh(col, 0.4));
  list.push({ x: x + 1, y: y + (h >> 1), h, col });
}
function renderCanyon(board) {
  const p = pix(W, H), r = rng(31), wins = [], strips = [], blinks = [];
  const inBB = (i, y) => board && i >= BB.x - 2 && i <= BB.x + BB.w + 1 && y >= BB.y - 8 && y <= BB.y + BB.h + 2;
  // south bank beyond the bridge, lower and lower towards the vanishing point
  [[62, 74, 102], [75, 90, 92], [91, 108, 82], [109, 128, 70], [129, 149, 58]].forEach(([a, b, top], k) => {
    facade(p, a, b, top, 150, r, wins, { body: k & 1 ? '#19152f' : '#1e1838' });
    strip(p, a + 2 + (k % 3) * 3, top + 5, 12 + k * 3, NEON[k % NEON.length], strips);
    if (k > 1) strip(p, b - 5, top + 14 + k * 2, 14, NEON[(k + 3) % NEON.length], strips);
    if (k & 1) { p.fill(a + 3, top - 5, 6, 5, '#241e40'); p.fill(a + 3, top - 5, 6, 1, '#3a3260'); }      // roof tank
    else { p.fill(a + 5, top - 7, 1, 7, '#2a2448'); blinks.push([a + 5, top - 8]); }
  });
  // the corner building at the bridge's south-east end: the billboard hangs on its front
  facade(p, 150, 205, 44, 150, r, wins, { body: '#1c1634', wx: 4, wy: 6, ww: 2, wh: 3, lit: 0.7, skip: inBB });
  for (let i = 151; i < 205; i += 2) p.set(i, 43, '#2e2850');                                   // roof railing
  p.fill(158, 36, 9, 7, '#262042'); p.fill(158, 36, 9, 1, '#443c6c'); p.fill(190, 30, 1, 14, '#2a2448'); blinks.push([190, 29]);
  for (let i = 152; i < 204; i++) { p.set(i, 96, '#3a3058'); p.set(i, 97, '#120e22'); }
  p.fill(154, 100, 48, 9, '#12091a');                                                           // a glyph fascia under the windows
  [0, 3, 6, 1, 4].forEach((g, k) => glyphP(p, G5[g], 158 + k * 9, 102, k & 1 ? '#ffe060' : '#ff6a4a'));
  strips.push({ x: 178, y: 104, h: 9, col: '#ff9a50' });
  // north bank: the house past the wheel, the wheel's own building, the corner building at the frame's edge
  facade(p, 48, 61, 78, 150, r, wins, { body: '#1a1634' });
  strip(p, 55, 86, 16, NEON[1], strips);
  facade(p, 17, 51, 64, 150, r, wins, { body: '#241c34', wx: 4, wy: 7, lit: 0.25 });
  facade(p, 0, 16, 48, 150, r, wins, { body: '#15122a', wx: 4, wy: 6, ww: 2, wh: 3 });
  strip(p, 10, 58, 30, NEON[0], strips);
  renderWheel(p);
  return { c: trim(p), wins, strips, blinks };
}

/* ================= The oval Ferris wheel on its building front (no mascot: a plain sun disc in the middle) ================= */
const GOND = ['#ffd040', '#ff5a4a'];                  // two colours in turn, so eight spacings per loop map the wheel onto itself
function renderWheel(p) {
  const { cx, cy, a, b } = WHEEL;
  for (const y of [50, 60, 72, 86, 100, 112]) for (let i = cx - a + 1; i < cx + a; i++) if (y > 63 ? (i & 1) === 0 : true) p.set(i, y, '#4a3c26');   // struts
  p.fill(cx, cy - b, 1, 2 * b, '#6a5424');
  for (let k = 0; k < 500; k++) {                     // the yellow oval track, two pixels thick
    const th = TAU * k / 500, s = Math.sin(th), c = Math.cos(th);
    p.set(cx + (a + 1) * s, cy - (b + 1) * c, s < -0.2 ? '#ffe070' : '#d8a428');
    p.set(cx + a * s, cy - b * c, '#8a6418');
  }
  p.ell(cx, 80, 6, 6, (i, j, dx, dy) => Math.hypot(dx, dy) > 0.72 ? '#fff0c0' : (i + j) % 5 === 0 ? '#ff6a5a' : '#e8343a');
}
function drawWheel(t) {
  const { cx, cy, a, b, n } = WHEEL, rot = 8 * t / LOOP, ch = Math.floor(t * 6);
  for (let k = 0; k < 44; k++) {                      // bulbs chasing round the oval
    const m = (k + ch) % 4;
    if (m > 1) continue;
    const th = TAU * k / 44;
    rect(ctx, Math.round(cx + a * Math.sin(th)), Math.round(cy - b * Math.cos(th)), 1, 1, m ? '#ffc840' : '#fff8d0');
  }
  for (let k = 0; k < n; k++) {                       // gondolas ride the outside of the track
    const th = TAU * (k + rot) / n, x = Math.round(cx + (a + 4) * Math.sin(th)), y = Math.round(cy - (b + 4) * Math.cos(th));
    rect(ctx, x - 2, y - 2, 5, 5, GOND[k % 2]); rect(ctx, x - 1, y - 1, 3, 1, '#fff4cc'); rect(ctx, x - 2, y + 2, 5, 1, '#3a2c20'); rect(ctx, x, y - 3, 1, 1, '#8a6418');
  }
  halo(HL.wheel, cx, 76, 0.8 + 0.1 * wave(t, 12));
}

/* ================= The billboard: an invented kushikatsu bar's sign, floodlit from above ================= */
// The city's own ad: KUSHI in cream on lacquer red, a skewer dipping (once!) into the sauce tin, and a ticker that
// spells the house rule. render(t, { pitch }) paints the site's faces on it instead; { billboard: false } leaves the
// sign out and the corner building keeps its windows.
const BB_LAMPS = [8, 25, 42], TICK = 'NO DOUBLE DIP! KUSHIKATSU BAR  ', TICK_W = TICK.length * 4;
function renderBBFrame() {
  const p = pix(W, H), x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h;
  p.fill(x0, y0, BB.w + 2, BB.h + 2, '#1a1428');
  for (let i = x0; i <= x1; i++) { p.set(i, y0, '#4a4068'); p.set(i, y1, '#2c2440'); }
  for (let y = y0; y <= y1; y++) { p.set(x0, y, '#342c50'); p.set(x1, y, '#110d1e'); }
  for (const lx of BB_LAMPS) { p.fill(BB.x + lx, y0 - 6, 1, 5, '#3a3258'); p.fill(BB.x + lx - 1, y0 - 2, 3, 1, '#5a5278'); }
  for (const bx of [x0 + 5, x1 - 5]) p.fill(bx, y1 + 1, 1, 3, '#2a2442');
  return trim(p);
}
function renderBBAd() {
  const p = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    const lamp = Math.max(...BB_LAMPS.map(lx => 1 - Math.hypot((i - lx) / 13, j / 24)));
    p.set(i, j, pick(['#5a0c18', '#7c1420', '#a01c28', '#c22a30'], 0.15 + 0.8 * Math.max(0, lamp), i, j));
  }
  for (let i = 0; i < BB.w; i++) { p.set(i, 0, '#ffd870'); p.set(i, BB.h - 1, '#8a5a1e'); }
  p.txt('KUSHI', 12, 3, '#3a0810', 2); p.txt('KUSHI', 11, 2, '#fff2d8', 2);
  p.fill(11, 13, 38, 6, '#1a0a10');
  const tin = pix(8, 6);
  tin.fill(0, 0, 8, 6, '#15100c'); tin.fill(0, 0, 8, 1, '#d8b060'); tin.fill(1, 1, 6, 1, '#4a2410'); tin.fill(0, 1, 1, 5, '#3a3020');
  const tick = pix(TICK_W + 40, 5);
  tick.txt(TICK + TICK.slice(0, 10), 0, 0, '#ffd23a');
  return { ad: p.done(), tin: tin.done(), tick: tick.done() };
}
function drawBillboard(t, pitch) {
  blit(BBF_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(BBAD.ad, BB.x, BB.y);
    ctx.drawImage(BBAD.tick, Math.floor(frac(14 * t / LOOP) * TICK_W), 0, 38, 5, BB.x + 11, BB.y + 14, 38, 5);
    const u = frac(t / 4), dip = u < 0.25 ? u / 0.25 : u < 0.5 ? 1 : u < 0.75 ? 1 - (u - 0.5) / 0.25 : 0, y = BB.y + 1 + Math.round(dip * 6);
    rect(ctx, BB.x + 5, y, 1, 11, '#ecd8a8');                                   // the skewer: two golden bites on a stick
    for (const [j, sauce] of [[1, false], [5, u >= 0.4]]) {
      rect(ctx, BB.x + 4, y + j, 3, 3, sauce ? '#8a4418' : '#e8a030'); rect(ctx, BB.x + 4, y + j, 1, 1, sauce ? '#c87a3a' : '#ffd878');
    }
    ctx.drawImage(BBAD.tin, BB.x + 2, BB.y + 13);
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {
    rect(ctx, BB.x + lx - 1, BB.y - 2, 3, 1, '#fff2d6');
    halo(HL.bbLamp, BB.x + lx, BB.y - 1, 0.9 + 0.1 * wave(t, 1700 + k * 130, k * 0.3));
  });
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + 6, 0.9);
}

/* ================= Per-frame: sky, far towers, the canyon ================= */
function drawScroll(cv, y, k, t) { const off = Math.floor(frac(k * t / LOOP) * W); ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); }
const blinkI = t => { const f = frac(t / 1.5); return f < 0.1 ? f / 0.1 : Math.max(0, 1 - (f - 0.1) / 0.45); };   // 160 per loop
function drawWins(wins, t, salt) {
  for (const w of wins) if (hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) rect(ctx, w.x, w.y, w.w, w.h, w.col);
}
function drawSky(t, flash) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e6e2ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9); ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7);
  if (flash > 0) { halo(HL.sheet, 300, 18, flash); halo(HL.sheet, 150, 30, flash * 0.6); }      // heat lightning inside the clouds
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
}
function drawCanyon(t, pitch, board) {
  const can = board ? CAN : CAN_BARE;
  blit(can.c);
  drawWins(can.wins, t, 40);
  can.strips.forEach((s, k) => {
    if (hash(k, step(t, 480), 21) < 0.04) rect(ctx, s.x - 1, s.y - (s.h >> 1), 3, s.h, '#1a1226');   // a strip drops out for half a second
    else halo(HL.neon[Math.max(0, NEON.indexOf(s.col))], s.x, s.y, 0.7 + 0.2 * wave(t, 900 + k * 70, k * 0.3));
  });
  drawWheel(t);
  if (board) drawBillboard(t, pitch);
  const I = blinkI(t);
  if (I > 0.02) {
    ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020';
    for (const [x, y] of [...FAR.blinks, ...can.blinks]) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); }
  }
}

/* ================= The sign wall (south bank, just west of the bridge) ================= */
// Every sign here is invented: an octopus with a headband, a ramen screen, a beckoning cat, a pufferfish lantern,
// dumplings, a beer mug, a daruma. No running man, no crab, no brand.
const LED = { x: 270, y: 30, w: 46, h: 40 };          // the video screen
const CAT = { x: 270, y: 76, w: 46, h: 50 };          // the beckoning cat's board
const BAN = { x: 322, y: 22, w: 12, h: 104 };         // the tall glyph banner
const WAVE = { x: 337, y: 22, w: 34, h: 30 };         // SUSHI over rolling waves
const PUF = { x: 354, y: 77, r: 12 };                 // the pufferfish lantern
const TEAL = { x: 337, y: 102, w: 34, h: 24 };        // glyph board under it
const GYO = { x: 376, y: 31, w: 52, h: 44 };          // the dumpling board
const MUG = { x: 376, y: 81, w: 52, h: 45 };          // the beer board
const COLN = { x: 434, y: 34, w: 13, h: 92 };         // the KARAOKE column
const DAR = { x: 464, y: 60, r: 12 };                 // the daruma
const RAM = { x: 450, y: 96, w: 29, h: 30 };          // RAMEN
const FACES = [HERO, LED, CAT, BAN, WAVE, TEAL, GYO, MUG, COLN, RAM];
const inFace = (i, y) => FACES.some(f => i >= f.x - 2 && i < f.x + f.w + 2 && y >= f.y - 3 && y < f.y + f.h + 2);
function renderWall() {
  const p = pix(W, H), r = rng(77), wins = [];
  [[206, 266, 22], [267, 318, 26], [319, 372, 16], [373, 430, 27], [431, 479, 30]].forEach(([a, b, top], k) => {
    facade(p, a, b, top, STREET, r, wins, { body: ['#1b1632', '#161228', '#1e1836', '#18142c', '#1b1632'][k], wx: 4, wy: 6, ww: 2, wh: 3, lit: 0.65, skip: inFace });
    for (let i = a + 1; i < b; i += 2) p.set(i, top - 1, '#2c2650');                              // roof railing
    if (k === 1 || k === 2) { p.fill(a + 6, top - 7, 8, 6, '#241e40'); p.fill(a + 6, top - 7, 8, 1, '#3e3668'); p.fill(a + 7, top - 1, 1, 1, '#1a1630'); }
    if (k === 0 || k === 2) p.fill(b - 8, top - 9, 1, 9, '#2a2448');
  });
  for (const f of FACES) {                                                                       // sign boxes
    p.fill(f.x - 1, f.y - 1, f.w + 2, f.h + 2, '#120c1a');
    for (let i = f.x - 1; i <= f.x + f.w; i++) p.set(i, f.y - 1, '#3e3458');
  }
  p.fill(HERO.x - 2, HERO.y - 2, HERO.w + 4, HERO.h + 4, '#160e1e');                              // the octopus board's bulb frame
  HERO_BULBS.forEach(([x, y]) => p.set(x, y, '#6a4c1c'));
  for (const [x, y, w] of [[340, 56, 28], [452, 44, 24]]) { p.fill(x, y, w, 2, '#3a3454'); p.fill(x, y + 2, w, 1, '#15111f'); }   // brackets for the lantern and the daruma's ledge
  p.fill(452, 73, 25, 3, '#4a4264'); p.fill(452, 76, 25, 1, '#1a1628');
  return { c: trim(p), wins, blinks: [[258, 12], [364, 6]] };
}
const HERO_BULBS = [];
{
  const x0 = HERO.x - 1, x1 = HERO.x + HERO.w, y0 = HERO.y - 1, y1 = HERO.y + HERO.h;
  for (let i = x0; i <= x1; i += 3) HERO_BULBS.push([i, y0]);
  for (let y = y0 + 3; y <= y1; y += 3) HERO_BULBS.push([x1, y]);
  for (let i = x1 - 3; i >= x0; i -= 3) HERO_BULBS.push([i, y1]);
  for (let y = y1 - 3; y > y0; y -= 3) HERO_BULBS.push([x0, y]);
}

/* ---- the hero: a giant neon octopus cook in front of a turning sunburst ---- */
const BURSTS = [['#ffd23a', '#ff8a1e', '#fff2a0'], ['#3ad8ff', '#1a7ae8', '#c8f6ff'], ['#ff3ac8', '#8a2ae0', '#ffc0f0'], ['#1c2478', '#0e1248', '#5a6ae0']];
const HPIC = 80;                                      // rows of the picture; the caption band fills the rest
function renderBurst(pal, phase, night) {
  const p = pix(HERO.w, HPIC), cx = 27, cy = 30;
  for (let j = 0; j < HPIC; j++) for (let i = 0; i < HERO.w; i++) {
    const d = Math.hypot(i - cx, j - cy), sec = Math.floor((Math.atan2(j - cy, i - cx) / TAU + 1 + phase / 32) * 16) & 1;
    let col = d < 21 + bayer(i, j) * 3 ? pal[2] : sec ? pal[0] : pal[1];
    if (night && d > 24 && hash(i, j, 5) < 0.03) col = '#ffffff';
    p.set(i, j, col);
  }
  return p.done();
}
function renderOcto(f) {
  const p = pix(HERO.w, HPIC), cx = 27, R = '#ff4638', RL = '#ff8a6a', RD = '#c0242c', OUT = '#fff0e0';
  const body = (i, j, dx, dy) => (dx + dy > 0.62 ? RD : dx + dy < -0.72 ? RL : R);
  const tent = (x0, dir, len, ph) => {
    for (let s = 0; s <= 1; s += 0.02) {
      const x = x0 + s * s * 9 * dir + Math.sin(s * 5 + ph + f * TAU / 4) * 2.4 * s;
      const y = 39 + s * len - (s > 0.72 ? (s - 0.72) * len * 1.5 : 0), rr = 2.7 - s * 1.5;
      p.ell(x, y, rr, rr, (i, j, dx) => (dx > 0.4 ? RD : R));
      if (Math.round(s * 50) % 6 === 3 && s > 0.15) p.set(x - Math.sign(dir), y, '#ffc8b8');                 // suckers
    }
  };
  [[-12, -1.2, 26, 0], [-7, -0.8, 32, 1.3], [-2, -0.3, 35, 2.6], [3, 0.3, 35, 3.9], [8, 0.9, 31, 5.2]].forEach(([dx, dir, len, ph]) => tent(cx + dx, dir, len, ph));
  let tx = 0, ty = 0;                                 // the raised arm, holding a pick with a takoyaki ball
  for (let s = 0; s <= 1; s += 0.02) { tx = cx + 12 + s * 9 + Math.sin(s * 3 + f * TAU / 4) * 0.8; ty = 38 - s * 20 + s * s * 5; p.ell(tx, ty, 2.6 - s, 2.6 - s, (i, j, dx) => (dx > 0.4 ? RD : R)); }
  p.line(tx, ty, tx + 1, ty - 9, '#f4e2b0');
  p.ell(tx + 1, ty - 11, 4, 4, (i, j, dx, dy) => dy < -0.25 ? ((i + j) % 3 === 0 ? '#fff4d0' : '#5a2810') : dx + dy > 0.7 ? '#8a4a1c' : '#c87a30');
  p.set(tx - 1, ty - 13, '#4ac060'); p.set(tx + 3, ty - 12, '#4ac060');
  p.ell(cx, 27, 16, 15, (i, j, dx, dy) => (Math.hypot(dx, dy) > 0.92 ? OUT : body(i, j, dx * 0.8, dy)));   // the head
  for (let j = 16; j <= 19; j++) for (let i = cx - 16; i <= cx + 16; i++) if (p.alpha(i, j)) p.set(i, j, (i + j * 2) % 7 < 2 ? '#3a6ae8' : '#f8f8ff');   // headband
  p.fill(cx + 14, 14, 4, 4, '#f8f8ff'); p.line(cx + 17, 17, cx + 21, 22 + (f & 1), '#f8f8ff'); p.line(cx + 16, 18, cx + 18, 25 - (f & 1), '#d8dcf8');
  for (const ex of [cx - 7, cx + 6]) { p.ell(ex, 27, 3, 3.6, '#ffffff'); p.fill(ex - 1 + (f === 2 ? 1 : 0), 26, 2, 3, '#1a1020'); p.set(ex - 1 + (f === 2 ? 1 : 0), 26, '#8a90c0'); }
  p.fill(cx - 13, 32, 3, 2, '#ff9ab0'); p.fill(cx + 10, 32, 3, 2, '#ff9ab0');
  p.ell(cx, 36, 3, 2.4, (i, j, dx, dy) => (Math.hypot(dx, dy) > 0.5 ? '#ffb8a0' : '#7a1418'));
  return { c: p.done(), tip: [Math.round(tx + 1), Math.round(ty - 16)] };
}
function renderCaption(lit) {
  const p = pix(HERO.w, HERO.h - HPIC);
  p.fill(0, 0, HERO.w, HERO.h - HPIC, '#fff4e0'); p.fill(0, 0, HERO.w, 1, '#c8242c');
  [1, 4, 6, 2].forEach((g, k) => glyphP(p, G7[g], 7 + k * 11, 2, lit ? '#e8202c' : '#e8b8b0'));
  p.txt('TAKO', 13, 11, '#9ab0f0', 2); p.txt('TAKO', 12, 10, '#1a3ad0', 2);
  return p.done();
}
function drawHero(t) {
  const fr = frac(t / 15), sc = step(t, 16) % 4, ph = Math.floor(t * 4) & 3, { x, y } = HERO;
  ctx.drawImage(BURST[sc][ph], x, y);
  if (fr > 0.95) { const hp = Math.round((fr - 0.95) / 0.05 * HPIC); ctx.drawImage(BURST[(sc + 1) % 4][ph], 0, 0, HERO.w, hp, x, y, HERO.w, hp); }   // the next sky wipes in
  const O = OCTO[Math.floor(t * 3) & 3];
  ctx.drawImage(O.c, x, y);
  if (frac(t / 6) > 0.97) { rect(ctx, x + 17, y + 24, 7, 7, '#ff4638'); rect(ctx, x + 30, y + 24, 7, 7, '#ff4638'); rect(ctx, x + 17, y + 28, 7, 1, '#7a1418'); rect(ctx, x + 30, y + 28, 7, 1, '#7a1418'); }   // a blink
  for (let k = 0; k < 4; k++) {                       // steam off the takoyaki
    const { u } = cycle(t, 80 + k * 9, k * 0.27), sx = x + O.tip[0] + Math.round(wave(t, 40 + k * 7, k * 0.3) * 1.5 * u), sy = y + O.tip[1] - Math.round(u * 9);
    if (sy > y) { ctx.globalAlpha = 0.7 * (1 - u); rect(ctx, sx, sy, 1 + (k & 1), 1, '#ffffff'); }
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(CAPTION[0], x, y + HPIC);
  const g = step(t, 480) % 6;                         // the glyphs light one by one, then all together
  if (g < 4) ctx.drawImage(CAPTION[1], 7 + g * 11, 2, 7, 7, x + 7 + g * 11, y + HPIC + 2, 7, 7);
  else ctx.drawImage(CAPTION[1], 0, 2, HERO.w, 7, x, y + HPIC + 2, HERO.w, 7);
  const ch = Math.floor(t * 8);
  ctx.fillStyle = '#ffe9a0';
  HERO_BULBS.forEach(([bx, by], k) => { if ((k + ch) % 3 === 0) ctx.fillRect(bx, by, 1, 1); });
}

/* ---- the video screen: a bowl of ramen, noodles lifted again and again; then two big glyphs ---- */
function renderStripes() {
  const p = pix(LED.w + 8, LED.h);
  for (let j = 0; j < LED.h; j++) for (let i = 0; i < LED.w + 8; i++) p.set(i, j, ((i + j) & 7) < 4 ? '#0c1c50' : '#16307c');
  return p.done();
}
function renderRamen(f) {
  const p = pix(LED.w, LED.h), lift = 5 + f * 4, top = 23 - lift;
  for (let j = 24; j <= 36; j++) {                                    // the bowl: red, a white key band, a small foot
    const hw = j > 34 ? 5 : Math.round(15 - (j - 24) * 0.75);
    for (let i = 23 - hw; i <= 23 + hw; i++) p.set(i, j, j === 28 ? ((i & 3) < 2 ? '#f4f0e8' : '#d8242c') : i < 23 - hw + 2 ? '#ff5a4a' : i > 23 + hw - 3 ? '#8a1420' : '#d8242c');
  }
  p.ell(23, 23, 15, 3.4, (i, j, dx, dy) => (Math.hypot(dx, dy) > 0.78 ? '#f8f4ec' : '#e8b458'));   // rim and broth
  p.fill(13, 22, 4, 3, '#8a4a2a'); p.fill(14, 22, 2, 1, '#c88a5a');                                   // pork
  p.ell(31, 23, 2.4, 1.6, '#fff8f0'); p.set(31, 23, '#ffb020'); p.set(32, 23, '#ffb020');             // egg
  for (const [i, j] of [[19, 24], [26, 25], [28, 22], [22, 25]]) p.set(i, j, '#4ac060');
  for (let k = 0; k < 4; k++) for (let j = top; j < 23; j++) p.set(22 + k * 1.4 + Math.sin(j * 0.9 + k) * 1.1, j, k & 1 ? '#ffe9a0' : '#f4cc60');   // the noodles
  p.line(21, top + 1, 41, top - 11, '#f0dcb0'); p.line(22, top - 1, 42, top - 12, '#b89868');         // chopsticks
  return p.done();
}
function renderBigGlyphs(inv) {
  const p = pix(LED.w, LED.h);
  p.fill(0, 0, LED.w, LED.h, inv ? '#ffe060' : '#d8202c');
  [3, 6].forEach((g, k) => G7[g].forEach((row, j) => { for (let i = 0; i < 7; i++) if (row[i] === '#') p.fill(2 + k * 23 + i * 3, 9 + j * 3, 3, 3, inv ? '#d8202c' : '#ffe060'); }));
  return p.done();
}
function drawDigits(n, x0, y0, sc, col) {             // FONT3 digits at scale sc (the screen's countdown)
  ctx.fillStyle = col;
  let cx = x0;
  for (const ch of String(n)) {
    FONT3[ch].forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') ctx.fillRect(cx + i * sc, y0 + j * sc, sc, sc); });
    cx += 4 * sc;
  }
}
function drawLED(t) {
  const { x, y, w, h } = LED;
  if (t >= SHOW.cd && t < SHOW.off) {                 // the countdown to the wall's show: 10, 9, 8 ...
    const n = Math.ceil(SHOW.off - t), tick = frac(SHOW.off - t) > 0.82;
    rect(ctx, x, y, w, h, tick ? '#fff6e0' : n <= 3 ? '#d8202c' : '#101a5a');
    if (n > 10) { ctx.drawImage(GLYPHS_C[step(t, 960) & 1], x, y); return; }
    drawDigits(n, x + (n > 9 ? 6 : 16), y + 5, n > 9 ? 5 : 6, tick ? '#d8202c' : '#ffe060');
    return;
  }
  const tau = t % 12;
  if (tau >= 8) { ctx.drawImage(GLYPHS_C[step(t, 480) & 1], x, y); return; }
  ctx.drawImage(STRIPES_C, Math.floor(t * 8) & 7, 0, w, h, x, y, w, h);
  ctx.drawImage(RAMEN_C[[0, 1, 2, 2, 1, 0, 0, 0][Math.floor(t * 4) & 7]], x, y);
  for (let k = 0; k < 5; k++) {                       // steam
    const { u } = cycle(t, 70 + k * 11, k * 0.21), sx = x + 14 + k * 4 + Math.round(wave(t, 30 + k * 5, k * 0.3) * 2 * u), sy = y + 20 - Math.round(u * 18);
    ctx.globalAlpha = 0.6 * Math.sin(Math.PI * u); rect(ctx, sx, sy, 2, 2, '#ffffff');
  }
  ctx.globalAlpha = 1;
}

/* ---- the beckoning cat ---- */
function renderCatBoard() {
  const p = pix(CAT.w, CAT.h);
  for (let j = 0; j < CAT.h; j++) for (let i = 0; i < CAT.w; i++)
    p.set(i, j, pick(['#6a0c1c', '#8e1424', '#b21e2c', '#d02c34'], 0.95 - Math.hypot((i - 18) / 30, (j - 24) / 30), i, j));
  [2, 5, 0].forEach((g, k) => glyphP(p, G5[g], 39, 9 + k * 12, '#ffd860'));                       // a gold glyph column
  // the cat: ears, head, collar and bell, body, the coin it hugs
  const W0 = '#fff8ec', WS = '#d4ccc8', X = 4, Y = 7;
  for (const ex of [X + 5, X + 20]) for (let j = 0; j < 6; j++) for (let i = -j * 0.6; i <= j * 0.6; i++) p.set(ex + i, Y + j, Math.abs(i) < j * 0.3 && j > 2 ? '#ff7a8a' : W0);
  const fur = (i, j, dx, dy) => (dx + dy > 0.75 ? WS : W0);
  p.ell(X + 13, Y + 31, 11, 10, fur); p.ell(X + 13, Y + 13, 12, 9.5, fur);
  for (const ex of [X + 8, X + 18]) { p.set(ex - 1, Y + 13, '#2a1a1a'); p.set(ex, Y + 12, '#2a1a1a'); p.set(ex + 1, Y + 13, '#2a1a1a'); }   // happy eyes
  p.set(X + 13, Y + 15, '#ff8a9a'); p.set(X + 12, Y + 17, '#2a1a1a'); p.set(X + 14, Y + 17, '#2a1a1a'); p.set(X + 13, Y + 16, '#2a1a1a');
  for (const s of [-1, 1]) { p.line(X + 13 + s * 6, Y + 15, X + 13 + s * 11, Y + 14, '#a89890'); p.line(X + 13 + s * 6, Y + 17, X + 13 + s * 11, Y + 18, '#a89890'); }
  p.fill(X + 5, Y + 21, 17, 2, '#e8202c'); p.ell(X + 13, Y + 24, 2, 2, '#ffd040'); p.set(X + 13, Y + 25, '#8a5a10');
  p.ell(X + 7, Y + 32, 4, 6.5, (i, j, dx, dy) => (Math.hypot(dx, dy) > 0.7 ? '#b87a10' : '#ffd040'));  // the coin
  glyphP(p, ['.#.', '###', '.#.', '###', '.#.'], X + 6, Y + 30, '#8a5a10');
  p.fill(X + 3, Y + 37, 5, 3, W0); p.fill(X + 16, Y + 38, 6, 3, W0);                                  // feet
  return p.done();
}
function drawCat(t) {
  const { x, y } = CAT, up = [0, 1, 2, 1][Math.floor(t * 4) & 3], py = y + 9 + up * 4;
  ctx.drawImage(CATB_C, x, y);
  rect(ctx, x + 29, py + 4, 4, y + 31 - py - 4, '#fff8ec'); rect(ctx, x + 32, py + 4, 1, y + 31 - py - 4, '#d4ccc8');      // the beckoning arm
  rect(ctx, x + 28, py, 6, 5, '#fff8ec'); rect(ctx, x + 29, py + 3, 1, 2, '#ff9aa8'); rect(ctx, x + 31, py + 3, 1, 2, '#ff9aa8');
  const s = step(t, 480);
  ctx.fillStyle = '#fff6c0';                           // gold sparkles
  for (let k = 0; k < 5; k++) if (hash(k, s, 71) < 0.5) { const sx = x + 2 + Math.floor(hash(k, s, 72) * 34), sy = y + 2 + Math.floor(hash(k, s, 73) * 46); ctx.fillRect(sx, sy - 1, 1, 3); ctx.fillRect(sx - 1, sy, 3, 1); }
}

/* ---- the tall glyph banner and the KARAOKE column: cells that light in turn ---- */
function renderBanner(lit) {
  const p = pix(BAN.w, BAN.h);
  p.fill(0, 0, BAN.w, BAN.h, '#1a0a18');
  for (let k = 0; k < 8; k++) glyphP(p, G7[k], 3, 3 + k * 13, lit ? (k & 1 ? '#ff7ab0' : '#fff0f6') : '#4a1a34');
  return p.done();
}
const KCOL = ['#ff5a8a', '#ffe060', '#5ee8ff', '#7dff8a', '#ff9a40', '#c890ff', '#ff5a8a'];
function renderColumn(lit) {
  const p = pix(COLN.w, COLN.h);
  p.fill(0, 0, COLN.w, COLN.h, '#150a20');
  [...'KARAOKE'].forEach((ch, k) => p.txt(ch, 4, 2 + k * 13, lit ? KCOL[k] : sh(KCOL[k], 0.28), 2));
  return p.done();
}
function drawCells(pair, f, t, n, salt) {             // cells light one after another, hold, blink
  const s = step(t + salt, 960) % 16, ch = f.h / n;
  ctx.drawImage(pair[0], f.x, f.y);
  if (s === 13) return;
  const k = s < n ? s + 1 : n;
  ctx.drawImage(pair[1], 0, 0, f.w, Math.round(k * ch), f.x, f.y, f.w, Math.round(k * ch));
}

/* ---- SUSHI over rolling waves, the pufferfish lantern, the glyph board ---- */
function renderWaveBoard() {
  const p = pix(WAVE.w, WAVE.h);
  for (let j = 0; j < WAVE.h; j++) for (let i = 0; i < WAVE.w; i++) p.set(i, j, pick(['#0a1040', '#101a5c', '#182878'], j / WAVE.h, i, j));
  p.ell(27, 6, 3.5, 3.5, '#ff4a3a');
  p.txt('SUSHI', 3, 4, '#0a1040'); p.txt('SUSHI', 2, 3, '#ffffff');
  const strip = pix(WAVE.w + 8, 6);                   // one row of crests, 8 px apart
  for (let i = 0; i < WAVE.w + 8; i++) {
    const m = i & 7, top = [2, 1, 0, 0, 1, 2, 3, 3][m];
    for (let j = top; j < 6; j++) strip.set(i, j, j === top ? '#eaf8ff' : j === top + 1 ? '#5ab8ff' : '#2470d8');
  }
  return { board: p.done(), strip: strip.done() };
}
function renderPuffer() {                             // a paper pufferfish lantern, facing left
  const R = PUF.r, p = pix(R * 2 + 9, R * 2 + 3), cx = R + 2, cy = R + 1;
  p.ell(cx, cy, R, R - 1.5, (i, j, dx, dy) => {
    if (Math.hypot(dx, dy) > 0.9) return (i + j) % 3 === 0 ? '#fffbe8' : '#e8c890';              // prickly rim
    if (dy > 0.1) return dx + dy > 0.9 ? '#e8d4a8' : '#fff6dc';                                    // the pale belly
    return hash(i >> 1, j >> 1, 5) < 0.3 ? '#8a5a30' : dy < -0.5 ? '#d8a868' : '#c08a4c';          // mottled back
  });
  for (let k = 0; k < 14; k++) { const a = TAU * k / 14; p.set(cx + Math.cos(a) * (R + 1.3), cy + Math.sin(a) * (R - 0.2), '#fff6dc'); }
  p.ell(cx - 6, cy - 2, 2.2, 2.2, (i, j, dx, dy) => (Math.hypot(dx, dy) > 0.6 ? '#ffffff' : '#141018'));
  p.fill(cx - R - 1, cy + 1, 2, 2, '#ff8a9a');                                                     // puckered mouth
  for (let j = -4; j <= 4; j++) p.fill(cx + R, cy + j, 5 - Math.abs(j), 1, Math.abs(j) & 1 ? '#c08a4c' : '#e8c890');   // tail
  p.fill(cx - 1, 0, 4, 2, '#c08a4c');                                                              // dorsal fin
  return p.done();
}
function renderTeal() {
  return [0, 1].map(on => {
    const p = pix(TEAL.w, TEAL.h);
    for (let j = 0; j < TEAL.h; j++) for (let i = 0; i < TEAL.w; i++) p.set(i, j, pick(['#083038', '#0c4a50', '#12686a'], 1 - j / TEAL.h, i, j));
    [1, 4, 7, 3].forEach((g, k) => glyphP(p, G5[g], 3 + k * 8, 3, on ? '#f0fff8' : '#58a8a0'));
    [6, 0, 5, 2].forEach((g, k) => glyphP(p, G5[g], 3 + k * 8, 14, on ? '#58a8a0' : '#ffe060'));
    return p.done();
  });
}
/* ---- dumplings, the beer mug, the daruma, RAMEN ---- */
function renderGyoza() {
  const p = pix(GYO.w, GYO.h);
  for (let j = 0; j < GYO.h; j++) for (let i = 0; i < GYO.w; i++) p.set(i, j, pick(['#06181c', '#0a2a2e', '#103c3c'], 1 - Math.hypot((i - 26) / 34, (j - 28) / 26), i, j));
  p.ell(26, 33, 23, 5, (i, j, dx, dy) => (Math.hypot(dx, dy) > 0.8 ? '#f0f8ff' : dy > 0.2 ? '#3a6ad0' : '#6a9af0'));   // the plate
  for (const [gx, gy] of [[12, 30], [26, 29], [40, 30]]) for (let j = -9; j <= 0; j++) {
    const hw = Math.round(8 * Math.sqrt(1 - (j / 9.5) ** 2));
    for (let i = -hw; i <= hw; i++) {
      const edge = Math.abs(i) === hw || j === -9 || (j / 9.5) ** 2 + (i / 8) ** 2 > 0.72;
      p.set(gx + i, gy + j, edge ? ((i + 20) % 3 === 0 ? '#b87830' : '#fff4d0') : i < -1 && j < -3 ? '#e8a850' : i + j > 2 ? '#d8c088' : '#f6e4b4');
    }
  }
  const txt = c => { const q = pix(38, 10); q.txt('GYOZA', 0, 0, c, 2); return q.done(); };
  return { board: p.done(), txt: [txt('#ff4a3a'), txt('#ffe060')] };
}
function renderMug() {
  const p = pix(MUG.w, MUG.h);
  for (let j = 0; j < MUG.h; j++) for (let i = 0; i < MUG.w; i++) p.set(i, j, pick(['#0a1038', '#101a50', '#182468'], 1 - Math.hypot((i - 14) / 36, (j - 22) / 30), i, j));
  for (let j = 7; j <= 39; j++) { p.set(4, j, '#c8f4ff'); p.set(21, j, '#c8f4ff'); }              // the glass, as neon tube
  for (let i = 4; i <= 21; i++) { p.set(i, 39, '#c8f4ff'); p.set(i, 40, '#5aa8c8'); }
  for (let j = 14; j <= 30; j++) { p.set(27, j, '#c8f4ff'); } for (let i = 22; i <= 27; i++) { p.set(i, 14, '#c8f4ff'); p.set(i, 30, '#c8f4ff'); }
  p.txt('BAR', 31, 6, '#3a2a08', 2); p.txt('BAR', 30, 5, '#ffe060', 2);
  [5, 1].forEach((g, k) => glyphP(p, G7[g], 31 + k * 10, 22, '#ff7ab0'));
  [2, 7, 4].forEach((g, k) => glyphP(p, G5[g], 31 + k * 7, 35, '#5ee8ff'));
  return p.done();
}
function drawMug(t) {
  const { x, y } = MUG, u = frac(t / 10), lvl = u < 0.6 ? u / 0.6 : u < 0.8 ? 1 : 1 - (u - 0.8) / 0.2, top = Math.round(38 - lvl * 26);
  ctx.drawImage(MUG_C, x, y);
  rect(ctx, x + 5, y + top, 16, 39 - top, '#ffb020'); rect(ctx, x + 5, y + top, 3, 39 - top, '#ffd868');
  if (lvl > 0.9) { rect(ctx, x + 4, y + top - 4, 18, 4, '#fff8e0'); rect(ctx, x + 6, y + top - 6, 6, 2, '#fff8e0'); rect(ctx, x + 14, y + top - 5, 5, 1, '#fff8e0'); }   // the head of foam
  else rect(ctx, x + 5, y + top - 1, 16, 1, '#fff2c0');
  ctx.fillStyle = '#fff2c0';
  for (let k = 0; k < 6; k++) { const { cyc, u: b } = cycle(t, 90 + k * 13, k * 0.17), by = 38 - b * 30; if (by > top) ctx.fillRect(x + 6 + Math.floor(hash(k, cyc, 4) * 14), y + Math.round(by), 1, 1); }
}
function renderDaruma() {
  const R = DAR.r, p = pix(R * 2 + 1, R * 2 + 3), cx = R, cy = R + 1, K = '#1a1014';
  p.ell(cx, cy, R - 1, R, (i, j, dx, dy) => (dx + dy > 0.8 ? '#8a1018' : dx + dy < -0.8 ? '#ff6a5a' : '#d8202c'));
  p.ell(cx, cy - 3, 7, 5.4, (i, j, dx, dy) => (Math.hypot(dx, dy) > 0.84 ? '#ffd040' : '#fff0d8'));   // the face, rimmed in gold
  for (const s of [-1, 1]) { p.fill(cx + s * 4 - 2, cy - 7, 4, 1, K); p.set(cx + s * 6, cy - 6, K); }  // heavy brows
  p.fill(cx - 5, cy - 4, 3, 3, K); p.set(cx - 4, cy - 4, '#ffffff');                                 // one eye painted in
  p.fill(cx + 3, cy - 4, 3, 3, K); p.fill(cx + 4, cy - 3, 1, 1, '#fff0d8');                          // the other still waiting for its wish
  for (const s of [-1, 1]) { p.set(cx + s, cy, K); p.set(cx + s * 2, cy, K); p.set(cx + s * 3, cy - 1, K); }   // moustache
  p.set(cx, cy + 1, '#c8242c');
  glyphP(p, G5[4], cx - 2, cy + 5, '#ffd040');
  return p.done();
}
function renderRamenBoard(on) {
  const p = pix(RAM.w, RAM.h);
  p.fill(0, 0, RAM.w, RAM.h, '#200a10');
  const c = on ? '#ffe060' : '#ff4a3a';
  for (let i = 6; i <= 22; i++) p.set(i, 12, c);
  for (let j = 13; j <= 18; j++) { p.set(6 + (j - 12) * 0.9, j, c); p.set(22 - (j - 12) * 0.9, j, c); }
  for (let i = 11; i <= 17; i++) p.set(i, 19, c);
  p.txt('RAMEN', 5, 23, on ? '#ff4a3a' : '#ffe060');
  return p.done();
}

/* ================= Timed events (loop seconds) ================= */
const SHOW = { cd: 30, off: 42, on: 43.2, end: 55, back: 56.5 };      // the wall's show: countdown, blackout, colour waves, flash
const TOURS = [{ a: 2, b: 28, dir: 1 }, { a: 32, b: 58, dir: -1 }, { a: 62, b: 88, dir: 1 }, { a: 112, b: 138, dir: -1 }, { a: 206, b: 232, dir: 1 }];
const DONDOKO = { far: 138, a: 152, b: 198 };                         // the drum boat: lights far off, then the pass
const FIRE = { build: 80, a: 92, b: 109 };                            // fireworks down the canal
const SHOWER = { a: 204, full: 209, ease: 224, b: 231 };              // a warm evening shower (the city's own weather)
const SHEETS = [198.4, 203.1];                                        // heat lightning before it
/** how excited the crowd is, 0..1: boats passing, the show, the fireworks, the drum boat */
function buzz(t) {
  let v = 0;
  for (const b of TOURS) v = Math.max(v, 0.5 * env(t, b.a + 2, b.b - 4, 3));
  return Math.max(v, env(t, SHOW.cd + 2, SHOW.back, 2), env(t, FIRE.build + 1, FIRE.b, 2), env(t, DONDOKO.far + 2, DONDOKO.b - 6, 3));
}

/* ================= Per-frame: the sign wall ================= */
const SHOWC = ['#ff2a6a', '#ffb020', '#fff060', '#3aff8a', '#3ac8ff', '#c05aff'];
function drawShow(t) {
  if (t < SHOW.cd || t >= SHOW.back) return;
  const all = (col, a, skip) => { ctx.globalAlpha = a; ctx.fillStyle = col; for (const f of FACES) if (f !== skip) ctx.fillRect(f.x, f.y, f.w, f.h); ctx.globalAlpha = 1; };
  if (t < SHOW.off) {                                  // the countdown: the wall dims a little more with every tick
    const a = 0.08 + 0.5 * (t - SHOW.cd) / (SHOW.off - SHOW.cd) - (frac(SHOW.off - t) > 0.82 ? 0.3 : 0);
    if (a > 0) all('#05030c', a, LED);
  } else if (t < SHOW.on) all('#05030c', 0.94);        // blackout
  else if (t < SHOW.end) {                             // bands of colour run along the whole wall, flashing on the beat
    const s = t - SHOW.on, off = s * 70, beat = frac(s * 2);
    all('#05030c', 0.62);
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.62;
    for (const f of FACES) for (let bx = f.x; bx < f.x + f.w;) {
      const band = Math.floor((bx - off) / 22), nx = Math.max(bx + 1, Math.min(f.x + f.w, Math.ceil((band + 1) * 22 + off)));
      ctx.fillStyle = SHOWC[((band % 6) + 6) % 6]; ctx.fillRect(bx, f.y, nx - bx, f.h);
      bx = nx;
    }
    if (beat < 0.2) all('#ffffff', 0.45 * (1 - beat / 0.2));
    ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
  } else {                                             // a white flash dying away
    ctx.globalCompositeOperation = 'lighter';
    all('#ffffff', 0.8 * (1 - (t - SHOW.end) / (SHOW.back - SHOW.end)));
    ctx.globalCompositeOperation = 'source-over';
  }
}
/** the wall's light on everything around it: 1 normally, low in the blackout, pulsing in the show */
function wallLight(t) {
  if (t < SHOW.cd || t >= SHOW.back) return 1;
  if (t < SHOW.off) return 1 - 0.45 * (t - SHOW.cd) / (SHOW.off - SHOW.cd);
  if (t < SHOW.on) return 0.12;
  return t < SHOW.end ? 0.8 + 0.4 * (frac((t - SHOW.on) * 2) < 0.2 ? 1 : 0) : 1.3;
}
function drawSigns(t) {
  blit(WALL.c);
  drawWins(WALL.wins, t, 90);
  drawHero(t); drawLED(t); drawCat(t);
  drawCells(BAN_C, BAN, t, 8, 0); drawCells(COL_C, COLN, t, 7, 1.3);
  ctx.drawImage(WAVEB.board, WAVE.x, WAVE.y);          // three rows of crests rolling past each other
  for (let k = 0; k < 3; k++) {
    const o = Math.floor(t * (3 + k)) & 7;
    ctx.drawImage(WAVEB.strip, k & 1 ? 7 - o : o, 0, WAVE.w, 6, WAVE.x, WAVE.y + 12 + k * 6, WAVE.w, 6);
  }
  // the pufferfish lantern sways on its cords, a fin flaps
  const sw = Math.round(wave(t, 20, 0.1) * 1.6 + wave(t, 53, 0.4) * 0.6), px0 = PUF.x - PUF.r - 2 + sw, py0 = PUF.y - PUF.r - 1, fin = Math.floor(t * 4) & 1;
  for (const d of [-5, 5]) rect(ctx, PUF.x + d + (sw >> 1), 59, 1, py0 - 57, '#3a3450');
  ctx.drawImage(PUFFER_C, px0, py0);
  rect(ctx, px0 + PUF.r + 2, py0 + PUF.r + 2 + fin, 4, 2, '#e8c890'); rect(ctx, px0 + PUF.r + 3, py0 + PUF.r + 4 + fin, 2, 1, '#c08a4c');
  ctx.drawImage(TEAL_C[step(t, 240) & 1], TEAL.x, TEAL.y);
  // dumplings steaming on their plate, the letters flashing, bulbs chasing along the bottom
  ctx.drawImage(GYOZA.board, GYO.x, GYO.y);
  ctx.drawImage(GYOZA.txt[step(t, 480) & 1], GYO.x + 7, GYO.y + 2);
  for (let k = 0; k < 7; k++) {
    const { u } = cycle(t, 50 + k * 7, k * 0.19), rr = 1 + Math.floor(u * 2.5);
    ctx.globalAlpha = 0.5 * Math.sin(Math.PI * u);
    ctx.drawImage(PUFFS[rr], GYO.x + 8 + k * 6 + Math.round(wave(t, 24 + k * 5, k * 0.3) * 2 * u) - rr, GYO.y + 20 - Math.round(u * 14) - rr);
  }
  ctx.globalAlpha = 1;
  const ch = Math.floor(t * 6);
  for (let i = 2; i < GYO.w - 1; i += 3) rect(ctx, GYO.x + i, GYO.y + GYO.h - 3, 1, 1, ((i / 3 | 0) + ch) % 3 === 0 ? '#fff6c0' : '#6a5420');
  drawMug(t);
  // the daruma rocks on its ledge and winks
  const D = DARUMA_C, rock = wave(t, 16, 0.3) * 1.8;
  for (let j = 0; j < D.height; j++) ctx.drawImage(D, 0, j, D.width, 1, DAR.x - DAR.r + Math.round(rock * (1 - j / D.height)), DAR.y - DAR.r - 1 + j, D.width, 1);
  if (frac(t / 8) > 0.9) rect(ctx, DAR.x + 4 + Math.round(rock * 0.75), DAR.y - 3, 1, 1, '#1a1014');
  ctx.drawImage(RAMB_C[step(t, 320) & 1], RAM.x, RAM.y);
  ctx.fillStyle = '#ffd0c0';
  for (let k = 0; k < 3; k++) for (let j = 0; j < 7; j++) ctx.fillRect(RAM.x + 10 + k * 4 + Math.round(Math.sin(j * 1.2 + k + TAU * 60 * t / LOOP)), RAM.y + 3 + j, 1, 1);
  drawShow(t);
  const L = wallLight(t);
  halo(HL.hero, HERO.x + 27, HERO.y + 34, 0.85 * L); halo(HL.cyan, LED.x + 23, LED.y + 20, 0.7 * L); halo(HL.crim, CAT.x + 20, CAT.y + 25, 0.6 * L);
  halo(HL.puffer, PUF.x + sw, PUF.y, (0.8 + 0.15 * wave(t, 30, 0.2)) * L); halo(HL.warm, GYO.x + 26, GYO.y + 22, 0.6 * L);
  halo(HL.cyan, MUG.x + 16, MUG.y + 22, 0.5 * L); halo(HL.crim, DAR.x, DAR.y, 0.6 * L); halo(HL.pink, COLN.x + 6, COLN.y + 46, 0.6 * L);
}

/* ================= People ================= */
// Summer festival clothes: tees and shorts, yukata with wide obi, jinbei, a happi coat. Built once as small sprites:
// side views with a four-frame walk, back views for the people at the rail, front views for the cook and the bench.
//
// The crowd knob (CROWD, render(t, { crowd })): everyone who is not part of the story has a fixed rank in (0, 1), given
// once here at build time, and is drawn only while rank < crowd, together with whatever is theirs (umbrella, phone,
// camera flash, lantern, dog, waving arm; the canal's mirror image follows by itself). The lists (bridge, far river
// walk, walkers, boat passengers, diners) take their ranks from gold(), which thins a list evenly along its length.
// The groups standing on the boards have theirs in RANK, weighted by head count and spread left / centre / right, so
// a unit stays whole (a couple, a parent and child) and the rail empties evenly. The essential cast has rank 0, so it
// is there at every share above 0: the takoyaki cook and helper, the first two of their queue and whoever was just
// served, the busker, the goldfish man, the bench pair the cat begs from, the mother whose girl runs about, the
// delivery rider, the shop cooks and the waiter across the canal, the tour boats' guide, the drum boat's paddlers and
// drummer.
//
// At crowd 0 nobody is drawn, and what is only there because of somebody goes too: the busker's guitar, amp, case and
// notes, the trays, the goldfish bag, the rider's bike, the whole drum boat. What people use stays, lit and
// unattended: the stall with its griddle steaming, the bench, the goldfish tank and its stool, the shops across the
// canal. The cat stays on its crate (nobody to beg from), and the tour boats sail with their skipper alone, who is
// painted into the hull.
const gold = i => frac((i + 0.5) * 0.6180339887);
const RANK = {
  dadSon: 0.06,                                       // rail, left: the father with his boy on his shoulders
  scoopMum: 0.18,                                     // goldfish tank: the mother and the child beside her
  photo: 0.26,                                        // rail, centre: the photographer
  fans: 0.35,                                         // the busker's listeners: the woman with the fan and the bouncing child
  friends: 0.47,                                      // rail, centre left: two friends
  queue3: 0.56,                                       // the queue's third place (it shortens from the tail, never below two places)
  couple: 0.65,                                       // rail, right: the couple in yukata and jinbei
  scoopKid: 0.74,                                     // goldfish tank: the second child (always above scoopMum, never left alone)
  selfie: 0.82,                                       // rail, left: the selfie pair
  suit: 0.91,                                         // the office worker listening to the busker
  trav: 0.97,                                         // rail, right: the traveller filming
};
const SKIN = ['#f0c8a0', '#e4b088', '#c88c62'], HAIR = ['#17131a', '#2a1c16', '#4a3020', '#c89048', '#d86a9a', '#8a8a92'];
const STY = [
  { c: '#e8e4dc', l: '#2a3458', hr: 0, sk: 0, bag: '#c83a3a' },                               // 0 white tee, jeans, red tote
  { c: '#d83a3a', l: '#e0d4b4', hr: 1, sk: 1, shorts: 1, cap: '#2a3a8a' },                    // 1 red tee, shorts, navy cap
  { c: '#dfe4f0', l: '#22263a', hr: 0, sk: 0, tie: 1, brief: '#4a3222' },                     // 2 office worker, tie loosened
  { yuk: '#2a3a8a', pat: '#e8e4f0', obi: '#e8b030', hr: 0, sk: 0, bun: 1, fan: '#ff6a8a' },   // 3 indigo yukata, gold obi
  { yuk: '#f0e6dc', pat: '#e8507a', obi: '#c82a4a', hr: 1, sk: 0, bun: 1, fan: '#3a8ae0' },   // 4 white yukata, pink flowers
  { yuk: '#3a4a44', pat: '#5a6e64', obi: '#16161c', hr: 0, sk: 1, man: 1 },                   // 5 a man's dark jinbei
  { c: '#f0c040', l: '#3a3a48', hr: 3, sk: 0, pack: '#2a8a6a' },                              // 6 yellow tee, backpack, bleached hair
  { c: '#3aa890', l: '#ece8e0', hr: 0, sk: 2, skirt: 1, long: 1 },                            // 7 teal top, white skirt
  { c: '#22202c', l: '#22202c', hr: 4, sk: 0, long: 1, bag: '#f0f0f0' },                      // 8 all in black, pink hair
  { c: '#3a6ad0', l: '#c8b890', hr: 1, sk: 1, shorts: 1, hat: '#e8d8a0' },                    // 9 blue tee, straw hat
  { c: '#e8782c', l: '#f0ece4', hr: 0, sk: 0, happi: 1, shorts: 1 },                          // 10 festival happi
  { c: '#c8c8dc', l: '#3a4a70', hr: 2, sk: 0, long: 1, pack: '#d83a6a' },                     // 11 grey tee, pink backpack
  { yuk: '#c82a4a', pat: '#ffd860', obi: '#f0e6dc', hr: 0, sk: 0, bun: 1, fan: '#f0f0f0' },   // 12 red yukata
  { c: '#f4f0ea', l: '#1c1c26', hr: 5, sk: 1, cap: '#f4f0ea' },                               // 13 the cook: whites, a white cap
  { c: '#6a3a8a', l: '#2a2a36', hr: 0, sk: 0, hat: '#2a2430' },                               // 14 the busker
  { c: '#ff7ab0', l: '#4a6ad0', hr: 0, sk: 0, shorts: 1 },                                    // 15 child in pink
  { c: '#5ac8e8', l: '#e8e0c8', hr: 1, sk: 1, shorts: 1, cap: '#ffd040' },                    // 16 child in a yellow cap
  { yuk: '#6a3a9a', pat: '#f0d8ff', obi: '#f0c040', hr: 0, sk: 0, bun: 1, fan: '#fff0a0' },   // 17 purple yukata
  { yuk: '#2a6a5a', pat: '#7ab89a', obi: '#e8e0d0', hr: 5, sk: 1, man: 1 },                   // 18 an older man in a green jinbei
  { c: '#ff9a3a', l: '#3a4a3a', hr: 0, sk: 2, pack: '#3a3a8a', cap: '#e83a3a' },              // 19 a traveller with a big pack
  { c: '#f4f4f8', l: '#c83a5a', hr: 2, sk: 0, skirt: 1, long: 1, bag: '#ffd040' },            // 20 white blouse, red skirt
];
const DIM = { k: [4, 4, 4], 0: [4, 7, 7], 1: [5, 8, 8], 2: [5, 9, 10] }, PW = 15, PCX = 7;
/** a person sprite: S = 'k' (child), 0, 1, 2 (18, 21, 24 px); view 'side' (frames 0..3, facing right), 'back', 'front', 'sit' */
function person(st, S, view = 'side', f = 0, arms = true) {
  const [hH, tH, lH] = DIM[S], total = hH + tH + lH, p = pix(PW, total + 1);
  const skin = SKIN[st.sk], skinD = sh(skin, 0.8), hair = HAIR[st.hr], top = st.yuk || st.c, topD = sh(top, 0.7), leg = st.l || st.yuk, robe = st.yuk && !st.man;
  const side = view === 'side', y0 = side && !(f & 1) ? 1 : side ? 0 : 1, ty = y0 + hH, ly = ty + tH, n = total + 1 - ly;
  const R = (i, j, w, h, col) => p.fill(PCX + i, j, w, h, col);
  const bare = r => (st.shorts || st.skirt || st.man) && r >= 3;
  const dots = (x0, x1, a, b) => { for (let j = a; j < b; j++) for (let i = x0; i <= x1; i++) if (hash(i, j, st.sk * 7 + 3) < 0.2) R(i, j, 1, 1, st.pat); };
  if (side) {
    const swing = f === 0 ? 1 : f === 2 ? -1 : 0;
    if (robe) {                                        // a yukata: the hem down to the ankles, short steps in geta
      R(-2, ly, 5, n - 2, top); R(-2, ly, 1, n - 2, topD); dots(-1, 2, ly, ly + n - 2);
      R(-1 - swing, ly + n - 2, 2, 1, skinD); R(swing, ly + n - 2, 2, 1, skin);
      R(-2 - swing, ly + n - 1, 3, 1, '#2a1a14'); R(swing, ly + n - 1, 3, 1, '#3a241a');
    } else for (const far of [true, false]) {
      const dir = far ? -swing : swing, lifted = swing === 0 && far === (f === 1);
      for (let r = 0; r < n; r++) {
        const s = Math.round(dir * 2.4 * r / (n - 1)), x = (far ? -1 : 0) + s - (lifted && r >= n - 3 ? 1 : 0);
        if (lifted && r === n - 1) continue;
        const shoe = r === n - 1 || (lifted && r === n - 2);
        R(x, ly + r, shoe ? 3 : 2, 1, shoe ? '#16121a' : bare(r) ? (far ? skinD : skin) : far ? sh(leg, 0.72) : leg);
      }
    }
    if (st.skirt) R(-2, ly, 5, 4, leg);
    R(-2, ty, 5, tH, top); R(-2, ty, 1, tH, topD);
    if (st.yuk) { dots(-1, 2, ty, ty + tH); R(1, ty, 1, 2, st.pat); R(-2, ty + tH - 3, 5, 2, st.obi); if (robe) R(-3, ty + tH - 4, 1, 3, st.obi); }
    if (st.happi) { R(-2, ty, 5, 1, '#1a1a2a'); R(2, ty, 1, tH, '#1a1a2a'); R(-1, ty + 2, 2, 2, '#fff4e0'); R(-2, ty + tH - 2, 5, 1, '#1a1a2a'); }
    if (st.tie) R(2, ty + 1, 1, 3, '#c83a3a');
    if (st.pack) { R(-4, ty + 1, 2, tH - 3, st.pack); R(-4, ty + 1, 2, 1, sh(st.pack, 1.35)); }
    if (arms) {                                        // the near arm swings; whatever it carries swings with it
      const ax = swing, fx = swing * 2, wide = st.yuk ? 2 : 1;
      R(ax, ty + 1, wide, st.yuk ? 4 : 2, sh(top, 0.84)); R(fx, ty + 3, 1, tH - 5, st.yuk ? sh(top, 0.84) : skinD); R(fx, ty + tH - 2, 1, 1, skin);
      if (st.bag) { R(fx - 1, ty + tH - 1, 4, 4, st.bag); R(fx - 1, ty + tH - 1, 4, 1, sh(st.bag, 0.7)); }
      if (st.brief) { R(fx - 1, ty + tH - 1, 5, 3, st.brief); R(fx + 1, ty + tH - 2, 1, 1, '#1a1210'); }
      if (st.fan) { R(fx + 1, ty + tH - 5, 3, 3, st.fan); R(fx + 2, ty + tH - 2, 1, 1, '#e8d0a0'); }
    }
    R(-2, y0, 4, hH - 1, hair); R(-1, y0 + 1, 3, hH - 2, skin); R(-1, y0 + hH - 1, 2, 1, skinD);
    p.set(PCX + 1, y0 + 2, '#3a2420');
    if (st.long) { R(-2, y0 + 1, 1, hH + 1, hair); R(-3, y0 + 2, 1, hH - 1, hair); }
    if (st.bun) { R(-3, y0, 2, 2, hair); R(-3, y0, 1, 1, '#ff5a7a'); }
    if (st.cap) { R(-2, y0, 4, 1, st.cap); R(-2, y0 + 1, 1, 1, st.cap); R(2, y0 + 1, 1, 1, st.cap); }
    if (st.hat) { R(-2, y0, 4, 1, st.hat); R(-3, y0 + 1, 6, 1, st.hat); }
    return p.done();
  }
  const front = view !== 'back', sit = view === 'sit';
  // back / front views: a wider body, both legs
  if (robe) { R(-3, ly, 6, n - 2, top); R(-3, ly, 1, n - 2, topD); R(2, ly, 1, n - 2, topD); dots(-2, 1, ly, ly + n - 2); R(-2, ly + n - 2, 2, 1, skin); R(1, ly + n - 2, 2, 1, skin); R(-3, ly + n - 1, 3, 1, '#2a1a14'); R(0, ly + n - 1, 3, 1, '#2a1a14'); }
  else if (sit) { R(-3, ly - 1, 6, 2, leg); for (const lx of [-3, 1]) { R(lx, ly + 1, 2, n - 3, bare(3) ? skin : sh(leg, 0.8)); R(lx, ly + n - 2, 2, 1, '#16121a'); } }
  else for (const lx of [-2, 0]) { for (let r = 0; r < n; r++) R(lx, ly + r, 2, 1, r === n - 1 ? '#16121a' : bare(r) ? skin : leg); R(lx + (lx ? 0 : 1), ly, 1, n - 1, bare(3) ? skinD : sh(leg, 0.72)); }
  if (st.skirt) R(-3, ly, 6, 4, leg);
  R(-3, ty, 6, tH, top); R(-3, ty, 1, tH, topD); R(2, ty, 1, tH, topD);
  if (st.yuk) { dots(-2, 1, ty, ty + tH); R(-3, ty + tH - 3, 6, 2, st.obi); if (robe && !front) R(-2, ty + tH - 5, 4, 3, sh(st.obi, 1.2)); }
  if (st.happi && !front) { R(-3, ty, 6, 1, '#1a1a2a'); R(-1, ty + 2, 2, 3, '#fff4e0'); }
  if (st.pack && !front) { R(-2, ty + 1, 4, tH - 3, st.pack); R(-2, ty + 1, 4, 1, sh(st.pack, 1.35)); }
  if (front) { R(-1, ty, 2, 2, st.yuk ? st.pat : skinD); if (st.tie) R(0, ty + 1, 1, 4, '#c83a3a'); }
  if (arms) for (const ax of [-4, 3]) { R(ax, ty, 1, st.yuk ? 5 : 2, sh(top, 0.84)); R(ax, ty + (st.yuk ? 5 : 2), 1, tH - (st.yuk ? 6 : 3), st.yuk ? sh(top, 0.84) : skinD); R(ax, ty + tH - 1, 1, 1, skin); }
  R(-2, 1, 4, hH - 1, front ? skin : hair); R(-2, 1, 4, 1, hair); R(-1, hH, 2, 1, skinD);
  if (front) { R(-3, 1, 1, hH - 2, hair); R(2, 1, 1, hH - 2, hair); p.set(PCX - 2, hH - 2, '#3a2420'); p.set(PCX + 1, hH - 2, '#3a2420'); }
  if (st.long) { R(-3, 2, 1, hH + 1, hair); R(2, 2, 1, hH + 1, hair); if (!front) R(-2, hH - 1, 4, 3, hair); }
  if (st.bun) { R(-1, 0, 2, 1, hair); R(1, 0, 1, 1, '#ff5a7a'); }
  if (st.cap) R(-2, 1, 4, front ? 1 : 2, st.cap);
  if (st.hat) { R(-2, 0, 4, 1, st.hat); R(-3, 1, 6, 1, st.hat); }
  return p.done();
}
/** a far-off figure, 12 px, facing right: the bridge, the far river walk, the boats */
function tiny(st, f) {
  const p = pix(7, 12), skin = SKIN[st.sk], top = st.yuk || st.c, robe = st.yuk && !st.man;
  p.fill(2, 0, 3, 1, HAIR[st.hr]); p.fill(2, 1, 1, 2, HAIR[st.hr]); p.fill(3, 1, 2, 2, skin); p.fill(3, 3, 1, 1, sh(skin, 0.8));
  if (st.hat || st.cap) p.fill(2, 0, st.hat ? 4 : 3, 1, st.hat || st.cap);
  if (st.bun) p.set(1, 0, HAIR[st.hr]);
  p.fill(2, 4, 3, robe ? 6 : 5, top); p.fill(2, 4, 1, robe ? 6 : 5, sh(top, 0.7)); p.fill(3 + (f ? 0 : 1), 5, 1, 3, sh(top, 0.85));
  if (st.yuk) p.fill(2, 7, 3, 1, st.obi);
  if (st.pack) p.fill(1, 5, 1, 3, st.pack);
  const lc = st.shorts || st.man ? skin : st.l || top;
  if (robe) { p.fill(f ? 2 : 3, 10, 2, 2, f ? skin : '#2a1a14'); p.fill(3, 11, 2, 1, '#2a1a14'); }
  else if (f) { p.fill(3, 9, 2, 3, lc); p.fill(3, 9, 2, 1, st.l || top); p.fill(3, 11, 2, 1, '#16121a'); }
  else { p.fill(2, 9, 1, 3, lc); p.fill(4, 9, 1, 3, lc); p.fill(2, 9, 3, 1, st.l || top); p.fill(2, 11, 1, 1, '#16121a'); p.fill(4, 11, 2, 1, '#16121a'); }
  return p.done();
}

/* ================= Under the bridge: the canal running on east to the next bridge ================= */
const quayS = x => WATER - Math.round((BR.ab - x) * (WATER - VP.y) / (BR.ab - VP.x));     // the south quay's line, x in VP.x..BR.ab
const quayN = x => WATER - Math.round(x * (WATER - VP.y) / VP.x);                         // the north quay's, x in 0..VP.x
// Both banks are rows of blocks seen at a raking angle, so every line along them runs to the vanishing point. A column
// d px from VP.x shows depth z = D / d (z = 1 at the bank's near end: the abutment, the frame's left edge), and a point
// u units up stands u * qk * d rows above the quay there (a unit is the quay's drop below the vanishing point). A block
// spans depths z0..z1 and is h units tall: its facade along the canal, and its near side wall, square on to us, which
// shows only where it rises above the block in front. The projecting signs are square on to us as well.
const UBANKS = [
  { side: 1, D: BR.ab - VP.x, qk: (WATER - VP.y) / (BR.ab - VP.x), tall: 1, seed: 41 },     // the south bank, right of the gap
  { side: -1, D: VP.x, qk: (WATER - VP.y) / VP.x, tall: 1.5, seed: 47 },                     // the north bank, left of it
];
const UFAC = [['#221b40', '#251d46', '#1f1a3e'], ['#352a5e', '#3b3066', '#31285c']];   // dark and light facades, taken in turn
const USHOP = [['#5a2814', '#b8642a', '#f4a858'], ['#4e1640', '#a83a7e', '#f080c0'], ['#123444', '#24849c', '#6ad4ec'], ['#4e3e14', '#b08a2a', '#f4d27a']];
const UWIN = ['#ffc878', '#ffb060', '#ffd890', '#b8d0ff', '#ff9ad0'];        // each block lights mostly in one of these
const UHAZE = ['#120e2c', '#1a1240', '#28165a', '#3a1c66', '#52247a'];
const STOREY = 1, BAY = 0.06, WDEEP = 3, REACH = 0.6;    // a storey (units), a window bay (depth), how far a block runs back and a sign reaches out (units)
UBANKS.forEach(B => {
  const r = rng(B.seed);
  B.blocks = [];
  for (let z = 1, k = 0, h0 = 0; B.D / z > 2; k++) {
    const len = 0.22 + r() * 0.3;
    let h = 2.6 + r() * 3.2;
    if (Math.abs(h - h0) < 0.8) h = h0 + (h0 > 4 ? -1.2 : 1.2);
    const sz = z + len * (0.12 + r() * 0.3), s0 = 1.2 + r() * 0.5, s1 = Math.min(h * B.tall - 0.3, s0 + 1.3 + r() * 1.5);
    B.blocks.push({ id: B.seed * 31 + k, z0: z, z1: z + len, h: h * B.tall, win: Math.floor(r() * UWIN.length), fac: UFAC[(k + 1) & 1][Math.floor(r() * 3)], shop: USHOP[Math.floor(r() * USHOP.length)],
      sign: r() < 0.72 && s1 - s0 > 0.9 ? { z: sz, u0: s0, u1: s1, col: NEON[Math.floor(r() * NEON.length)] } : null });
    h0 = h; z += len;
  }
});
/** one block: its side wall, then its facade (shop fronts, rows of windows, a lit cornice), then its projecting sign */
function underBlock(P, B, b, signs, wins) {
  const set = (x, y, col) => { if (x < BR.ab && y >= deckY(x)) P.set(x, y, col); };   // the abutment and the deck hide the rest
  const p = { set, fill: (x, y, w, h, col) => { for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) set(i, j, col); } };
  const { side, D, qk } = B, X = d => VP.x + side * d, qrow = d => VP.y + qk * d;
  const dn = D / b.z0, df = D / b.z1, xn = Math.round(X(dn)), xf = Math.round(X(df));
  // the side wall: darker than the facade, its corner and top edge caught by the canal's glow
  const wTop = Math.round(qrow(dn) - qk * dn * b.h), wBot = Math.round(qrow(dn)), ww = Math.round(WDEEP * qk * dn);
  for (let i = 0; i <= ww; i++) for (let y = wTop; y < wBot; y++) p.set(xn + side * i, y, y === wTop ? mixc(b.fac, '#9a6ac8', 0.3) : i === 0 ? sh(b.fac, 1.4) : sh(b.fac, 0.62));
  const lit = {};                                     // the lit windows, gathered for the ones that switch on and off
  for (let x = xf; side > 0 ? x <= xn : x >= xn; x += side) {
    const d = Math.abs(x - VP.x);
    if (d < 1) continue;
    const z = D / d, qf = qrow(d), s = qk * d, top = Math.round(qf - s * b.h), base = Math.round(qf);
    const bp = (z - b.z0) / BAY, bi = Math.floor(bp), fb = bp - bi, bayPx = BAY * d * d / D, pier = bayPx >= 2.4 && (fb < 0.24 || fb > 0.76);
    const edge = x === xn ? 1.4 : x === xf ? 0.75 : 1, fac = mixc(b.fac, '#2c1a54', clamp(1 - d / 34, 0, 0.6));   // far off, the haze takes the facades
    for (let y = top; y < base; y++) {
      const u = (qf - y - 0.5) / s;
      let col = sh(fac, edge);
      if (y === top) col = mixc(fac, '#9a6ac8', 0.42);                      // the cornice, caught by the neon
      else if (u < 0.78 && u > 0.08) col = pier && bayPx >= 3 ? sh(fac, 0.75) : pick(b.shop, 1.05 - Math.abs(u - 0.4) * 1.6, x, y);   // shop fronts on the river walk
      else if (u >= 0.78 && u < 1) col = sh(fac, 0.8);                        // their fascia
      p.set(x, y, col);
    }
    if (pier || edge !== 1) continue;
    const fpx = s * STOREY, every = Math.max(1, Math.ceil(2.2 / fpx)), wh = fpx >= 6 ? 2 : 1, fade = clamp(1 - d / 26, 0, 0.7);
    for (let f = 0; 1 + (f + 0.8) * STOREY < b.h; f += every) {              // a window row per storey (every other one far off)
      const yc = Math.round(qf - s * (1 + (f + 0.45) * STOREY));
      if (yc - wh < top + 1) break;
      const on = hash(b.id, f, bi) < 0.64, wc = hash(b.id, f, bi + 99), col = on ? mixc(UWIN[wc < 0.75 ? b.win : Math.floor(wc * 13) % UWIN.length], fac, fade) : sh(fac, 0.55);
      for (let j = 0; j < wh; j++) p.set(x, yc - j, col);
      if (on && bayPx >= 3 && yc - wh >= deckY(x) + 8) { const key = f * 1000 + bi, w = lit[key] || (lit[key] = { x0: x, x1: x, y: yc - wh + 1, h: wh, col: css(col), f, bi }); w.x0 = Math.min(w.x0, x); w.x1 = Math.max(w.x1, x); }
    }
  }
  for (const w of Object.values(lit)) if (hash(b.id, w.f, w.bi + 7) < 0.35) {
    p.fill(w.x0, w.y, w.x1 - w.x0 + 1, w.h, sh(b.fac, 0.55));
    wins.push({ id: wins.length, x: w.x0, y: w.y, w: w.x1 - w.x0 + 1, h: w.h, n: 3 + Math.floor(hash(b.id, w.bi, 5) * 7), p: 0.66, off: hash(b.id, w.f, 6), col: w.col });
  }
  // the projecting sign: a lit box hung out over the walk, its frame dark, its glyphs a dither of bright and dim
  const S = b.sign;
  if (!S) return;
  const ds = D / S.z, w = Math.max(1, Math.round(REACH * qk * ds)), xs = Math.round(X(ds)), y0 = Math.round(qrow(ds) - qk * ds * S.u1), y1 = Math.round(qrow(ds) - qk * ds * S.u0);
  const x0 = side > 0 ? xs - w : xs + 1, h = y1 - y0, col = mixc(S.col, '#2c1a54', clamp(1 - ds / 40, 0, 0.55));
  if (h < 4) return;
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const frame = w >= 4 && h >= 6 && (i === 0 || i === w - 1 || j === 0 || j === h - 1);
    p.set(x0 + i, y0 + j, frame ? '#120c1a' : w >= 3 && hash(x0 + i, y0 + j, 7) > 0.62 ? sh(S.col, 0.45) : w < 3 && h >= 6 && (j % 3) === 2 ? sh(S.col, 0.6) : col);
  }
  signs.push({ x: x0 + (w >> 1), y: y0 + (h >> 1), x0, y0, w, h, col: S.col });
}
function renderUnder() {
  const p = pix(W, H), signs = [], wins = [];
  for (let x = 0; x < 206; x++) {
    const q = x < VP.x ? quayN(x) : x < BR.ab ? quayS(x) : FWALK, d = Math.abs(x - VP.x);
    for (let y = deckY(x); y < WATER; y++) {
      if (x >= BR.ab) p.set(x, y, (x + y) % 6 === 0 ? '#2a2444' : '#221d3a');                      // the abutment wall
      else if (y >= q) p.set(x, y, (x + y) & 1 ? '#070a1a' : '#0b0e24');                          // far water
      else p.set(x, y, pick(UHAZE, clamp(1.1 - Math.hypot(d / 80, (y - VP.y) / 34), 0, 1), x, y)); // haze
    }
  }
  for (const B of UBANKS) for (let k = B.blocks.length - 1; k >= 0; k--) underBlock(p, B, B.blocks[k], signs, wins);   // far to near
  // the far water doubles both banks, dimmer, broken by the ripples
  const at = (i, j) => { const o = (j * W + i) * 4; return [p.d[o], p.d[o + 1], p.d[o + 2]]; };
  for (let x = 0; x < BR.ab; x++) {
    const q = x < VP.x ? quayN(x) : quayS(x);
    for (let y = q + 1; y < WATER; y++) {
      const k = y - q, src = q - k, sx = Math.min(BR.ab - 1, x + ((y + (x >> 2)) % 3 === 0 ? 1 : 0));
      if (src < deckY(sx) + 9 || hash(x >> 1, y, 13) < 0.22) continue;
      p.set(x, y, mixc(at(sx, src), '#060918', clamp(0.42 + k * 0.05, 0, 0.8)));
    }
    if (Math.abs(x - VP.x) > 2) p.set(x, q, '#4a4468');
  }
  for (const [x0, x1, quay] of [[4, VP.x - 3, quayN], [VP.x + 3, BR.ab - 2, quayS]]) for (let x = x0; x < x1;) {   // lanterns along both river walks
    const d = Math.abs(x - VP.x);
    p.set(x, quay(x) - 1 - Math.round(d / 40), '#ffe8b0');
    x += Math.max(2, Math.round(d / 12));
  }
  for (let x = VP.x - 7; x <= VP.x + 9; x++) { p.set(x, VP.y - 3, '#6a5a90'); p.set(x, VP.y - 2, '#2a2248'); if ((x & 3) === 0) p.set(x, VP.y - 4, '#ffe0a0'); }   // the next bridge
  // the south abutment: the river walk runs on under the deck, posters on the wall
  for (let x = BR.ab; x < 206; x++) { p.set(x, FWALK, '#5a5478'); for (let y = FWALK + 1; y < WATER; y++) p.set(x, y, (x + y * 3) % 7 === 0 ? '#201c36' : '#2a2642'); }
  for (const [x, c] of [[158, '#c83a4a'], [168, '#e8c040'], [186, '#3a8ad0']]) { p.fill(x, 144, 7, 9, c); glyphP(p, G5[(x >> 2) % 8], x + 1, 146, '#fff4e0'); }
  return { ...trim(p), signs, wins };
}

/** the view under the bridge: windows switching on and off, the near signs glowing, one dropping out now and then */
function drawUnder(t) {
  blit(UNDER);
  drawWins(UNDER.wins, t, 120);
  const s = step(t, 480);
  UNDER.signs.forEach((g, k) => {
    if (g.w < 3) return;
    if (hash(k, s, 23) < 0.03) rect(ctx, g.x0, g.y0, g.w, g.h, '#1a1226');
    else halo(HL.neon[Math.max(0, NEON.indexOf(g.col))], g.x, g.y, 0.6 + 0.2 * wave(t, 700 + k * 60, k * 0.4));
  });
}

/* ================= Ebisubashi: the deck, its glass parapet, the lamps ================= */
const BR_LAMPS = [22, 74, 126, 178];
function renderBridge() {
  const p = pix(W, H);
  for (let x = 0; x < 206; x++) {
    const dy = deckY(x);
    p.set(x, dy - 7, '#e0dcf4'); p.set(x, dy - 6, '#8a86ac');                                   // hand rail
    for (let y = dy - 5; y < dy; y++) { if (x % 12 === 0) p.set(x, y, '#6a6690'); else p.set(x, y, (x + y) % 11 === 0 ? '#8a9ac8' : '#44527c', 105); }   // glass panels between posts
    p.set(x, dy, '#c4c0dc'); for (let y = dy + 1; y < dy + 5; y++) p.set(x, y, pick(['#5a5678', '#7a7698', '#8e8aac'], 0.7 - (y - dy) / 6 + (x % 24 < 12 ? 0.1 : 0), x, y));
    p.set(x, dy + 4, '#6a4a8a');                                                               // the underside catches the neon
    if (x < BR.ab + 8) for (let y = dy + 5; y < dy + 9; y++) p.set(x, y, x % 16 === 0 ? '#120e22' : y === dy + 5 ? '#2c2448' : '#1c1834');
  }
  for (const x of BR_LAMPS) { const dy = deckY(x); p.fill(x, dy - 24, 1, 17, '#3a3658'); p.fill(x - 2, dy - 25, 5, 1, '#3a3658'); p.fill(x - 1, dy - 24, 3, 2, '#fff4d0'); }
  return trim(p);
}

/* ================= The south bank's lower floor, the ramp, the far river walk and its lanterns ================= */
const rampY = x => STREET - 1 + Math.round((x - 204) * (FWALK - STREET) / 84);                 // the ramp's surface: row 131 at x = 204, 159 at 288
const RACK = { x0: 292, y: 137 };
// the diners painted into the izakaya's window (0) and the sushi counter's (1): each one's 4x5 box, thinned by drawing
// the empty window over it
const SHOPWIN = [{ x: 294, y: 140, w: 27, h: 14 }, { x: 342, y: 143, w: 41, h: 12 }];
const DINERS = [[297, 146, 0], [304, 146, 0], [310, 146, 0], [316, 146, 0], [347, 147, 1], [371, 147, 1]].map(([x, y, win], i) => ({ x, y, win, rank: gold(i) }));
function renderLower() {
  const p = pix(W, H), r = rng(88);
  for (let i = 206; i < W; i++) {
    p.set(i, STREET, '#5a5478'); p.set(i, STREET + 1, '#2c2846');
    for (let y = STREET + 2; y < FWALK; y++) p.set(i, y, (i + y) % 9 === 0 ? '#221c3a' : '#1b1632');
    p.set(i, FWALK, '#6a6488'); p.set(i, FWALK + 1, '#3e3a58');
    for (let y = FWALK + 2; y < WATER; y++) p.set(i, y, (i + y * 5) % 11 === 0 ? '#1e1a32' : y === WATER - 1 ? '#151226' : '#2a2642');
  }
  const warm = (x0, y0, w, h, ramp, q = p, ox = 0, oy = 0) => { for (let y = y0; y < y0 + h; y++) for (let i = x0; i < x0 + w; i++) q.set(i - ox, y - oy, pick(ramp, 1 - (y - y0) / h * 0.7 - Math.abs(i - x0 - w / 2) / w * 0.4, i, y)); };
  const AMBER = ['#a8561e', '#d8802c', '#f4aa48', '#ffd27a'], CREAM = ['#b08a50', '#e0c080', '#fff0c0', '#fffbe8'];
  const bare = SHOPWIN.map((s, k) => { const q = pix(s.w, s.h); warm(s.x, s.y, s.w, s.h, k ? CREAM : AMBER, q, s.x, s.y); return q.done(); });   // the two windows' light with nobody in front of it
  // an izakaya: red awning, a wide window with diners, a noren door
  p.fill(292, 135, 44, 3, '#b0242c'); for (let i = 292; i < 336; i += 4) p.fill(i, 138, 2, 1, '#b0242c');
  warm(294, 140, 27, 14, AMBER); p.fill(294, 151, 27, 1, '#5a3018');
  for (const hx of [298, 305, 311, 317]) { p.fill(hx, 146, 2, 2, '#2a1810'); p.fill(hx - 1, 148, 4, 3, '#3a2014'); }
  warm(324, 140, 10, 20, AMBER); p.fill(324, 139, 10, 6, '#1e2a5c'); p.fill(326, 141, 1, 2, '#f0ece0'); p.fill(331, 141, 1, 2, '#f0ece0'); p.fill(328, 139, 1, 6, '#141c40');
  // a sushi counter: white noren, the cook behind the glass
  warm(342, 143, 41, 12, CREAM); p.fill(342, 152, 41, 3, '#8a5a30'); p.fill(342, 152, 41, 1, '#c89050');
  for (let i = 342; i < 383; i++) if ((i - 342) % 8 !== 7) { p.fill(i, 137, 1, 6, '#f4f0e4'); if ((i - 342) % 8 === 3) p.fill(i, 139, 1, 2, '#c8242c'); }
  for (const hx of [348, 372]) { p.fill(hx, 147, 2, 2, '#241418'); p.fill(hx - 1, 149, 4, 3, '#2e2a3a'); }
  // a takoyaki shop: an open front, red lanterns, a counter with a glyph
  warm(388, 138, 41, 13, ['#c05a20', '#f08a30', '#ffb858', '#ffe090']); p.fill(388, 151, 41, 9, '#8a1c22'); p.fill(388, 151, 41, 1, '#d84a3a');
  [1, 5, 2].forEach((g, k) => glyphP(p, G5[g], 394 + k * 12, 153, '#ffe9b0'));
  for (const lx of [392, 408, 424]) { p.fill(lx, 138, 3, 4, '#e8342c'); p.fill(lx, 139, 3, 1, '#ff8a5a'); p.set(lx + 1, 142, '#2a1010'); }
  // a bar: a magenta window of bottles, a small sign, a vending machine
  warm(436, 141, 27, 13, ['#3a0f44', '#6a1c70', '#a02c98', '#d848b8']);
  for (let i = 438; i < 462; i += 3) p.fill(i, 148 - (i % 2), 1, 4 + (i % 2), ['#7dff8a', '#ffe060', '#5ee8ff', '#fff0f0'][Math.floor(r() * 4)]);
  p.fill(438, 152, 23, 1, '#2a0a30'); p.txt('BAR', 442, 134, '#5ee8ff');
  p.fill(468, 142, 10, 18, '#dfe6f2'); p.fill(469, 144, 8, 9, '#eaf6ff'); for (let i = 470; i < 477; i += 2) { p.fill(i, 145, 1, 3, ['#e8404a', '#3a6ae8', '#3ab86a', '#ff9a2a'][(i >> 1) & 3]); p.fill(i, 149, 1, 3, ['#f0d040', '#8a4ad0', '#e8404a', '#2ac8d8'][(i >> 1) & 3]); }
  for (const px0 of [290, 338, 386, 432]) p.fill(px0, STREET + 2, 2, FWALK - STREET - 2, '#2e2848');   // pillars between the fronts
  // the ramp: its wall with festival posters, the surface, a rail
  for (let i = 204; i <= 288; i++) {
    const ry = rampY(i);
    for (let y = ry + 2; y < FWALK; y++) p.set(i, y, (i + y * 2) % 8 === 0 ? '#2c2846' : '#353050');
    p.set(i, ry, '#9a96b8'); p.set(i, ry + 1, '#55506e'); p.set(i, ry - 6, '#7a769c');
    if (i % 7 === 0) for (let y = ry - 5; y < ry; y++) p.set(i, y, '#55506e');
  }
  for (const [x, c] of [[232, '#e8c040'], [246, '#c83a4a'], [262, '#3aa890']]) if (rampY(x) + 4 < 151) { p.fill(x, 151, 7, 8, c); glyphP(p, G5[(x >> 1) % 8], x + 1, 152, '#1a1020'); }
  for (let x = RACK.x0; x < W; x += 38) p.fill(x, RACK.y - 1, 1, FWALK - RACK.y + 1, '#4a3628');     // lantern rack posts
  for (let k = 0; k < 3; k++) for (let i = RACK.x0; i < W; i++) p.set(i, RACK.y + k * 4, '#3a2a22');
  // one wire's worth of lanterns, drawn three times per frame (they sway)
  const row = pix(W - RACK.x0, 3);
  for (let i = 1; i < W - RACK.x0 - 1; i += 4) if (i % 38 > 2) { row.fill(i, 0, 2, 1, '#fff6d8'); row.fill(i, 1, 2, 1, '#ffd890'); row.fill(i, 2, 2, 1, '#e8964a'); }
  return { c: trim(p), row: row.done(), bare };
}

/* ================= Per-frame: the crowd on the bridge ================= */
const BRW = [], BRL = [], UMB = ['#e83a4a', '#3a6ae8', '#f0f0f8', '#ffd040', '#2a2a3a', '#3ab890', '#ff8ac0', '#c8e4ff'];
{ const r = rng(201); for (let i = 0; i < 28; i++) BRW.push({ id: i, rank: gold(i), st: Math.floor(r() * 13), k: 6 + Math.floor(r() * 5), ph: r(), dir: r() < 0.5 ? 1 : -1, lane: i % 3 ? 0 : 1 }); }
{ const r = rng(202); for (let x = 9; x < 198; x += 12 + Math.floor(r() * 7)) BRL.push({ id: BRL.length, rank: gold(BRL.length), x, st: Math.floor(r() * 13), phone: r() < 0.6 }); }
/** how far an umbrella is open (0..1) during the shower: they pop open one after another, and fold when it eases */
function umbOpen(t, id) {
  const a = SHOWER.a + 2.5 + hash(id, 55) * 4.5, b = SHOWER.b - 4.5 + hash(id, 56) * 3.5;
  return t < a || t > b + 0.4 ? 0 : Math.min(1, (t - a) / 0.35, (b + 0.4 - t) / 0.4);
}
function drawUmb(x, y, id, o, big) {                   // y: the row of the canopy's rim
  if (o <= 0) return;
  const col = UMB[id % UMB.length], hw = Math.max(1, Math.round((big ? 6 : 3) * o));
  rect(ctx, x, y - (big ? 3 : 1), 1, big ? 9 : 4, '#c8c8d8');
  rect(ctx, x - hw, y, hw * 2 + 1, 1, col);
  if (big) { rect(ctx, x - hw + 1, y - 1, hw * 2 - 1, 1, col); rect(ctx, x - Math.max(1, hw - 2), y - 2, Math.max(1, hw - 2) * 2 + 1, 1, col); ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.fillRect(x - hw + 1, y - 1, 2, 1); }
  else rect(ctx, x - hw + 1, y - 1, Math.max(1, hw * 2 - 1), 1, col);
}
function drawBridgeCrowd(t, bz, shower, crowd) {
  const fr = Math.floor(t * 4);
  for (let lane = 1; lane >= 0; lane--) for (const w of BRW) {
    if (w.lane !== lane || w.rank >= crowd) continue;
    const u = frac(w.k * t / LOOP + w.ph), x = Math.round(w.dir > 0 ? -8 + u * 224 : 216 - u * 224), y = deckY(x) - 12 - lane;
    ctx.drawImage(TINY[w.st][(fr + w.id) & 1][w.dir > 0 ? 0 : 1], x - 3, y);
    if (shower) drawUmb(x, y - 2, w.id, umbOpen(t, w.id), false);
  }
  const s = step(t, 2400), ws = Math.floor(t * 5);
  for (const l of BRL) {                               // people leaning on the rail, facing the canal: phones up, a wave for the boats
    if (l.rank >= crowd) continue;
    const st = STY[l.st], y = deckY(l.x) - 13, wav = bz > 0.45 && hash(l.id, 31) < 0.3 + bz * 0.5;
    rect(ctx, l.x - 1, y, 3, 1, HAIR[st.hr]); rect(ctx, l.x - 1, y + 1, 3, 2, SKIN[st.sk]);
    rect(ctx, l.x - 2, y + 3, 5, 3, st.yuk || st.c); rect(ctx, l.x - 2, y + 5, 1, 1, SKIN[st.sk]); rect(ctx, l.x + 2, y + 5, 1, 1, SKIN[st.sk]);
    if (wav) rect(ctx, l.x + 3 + ((ws + l.id) & 1), y - 2, 1, 4, SKIN[st.sk]);
    else if (l.phone) rect(ctx, l.x + 2, y + 2, 1, 2, '#cfe8ff');
    if (shower) drawUmb(l.x, y - 2, l.id + 40, umbOpen(t, l.id + 40), false);
  }
  blit(BRIDGE_C);
  BR_LAMPS.forEach((x, k) => halo(HL.lamp, x, deckY(x) - 23, 0.85 + 0.1 * wave(t, 700 + k * 90, k * 0.3)));
  for (const l of BRL) if (l.phone && l.rank < crowd && hash(l.id, s, 33) < 0.006 + bz * 0.03) {      // camera flashes
    const y = deckY(l.x) - 11;
    rect(ctx, l.x + 1, y - 1, 3, 3, '#ffffff'); halo(HL.flash, l.x + 2, y, 1);
  }
}

/* ================= Per-frame: the far bank's lower floor, its river walk, the lantern rows ================= */
const FARW = [];
{ const r = rng(203); for (let i = 0; i < 13; i++) FARW.push({ id: i, rank: gold(i), st: Math.floor(r() * 13), k: 3 + Math.floor(r() * 3), ph: r(), dir: i & 1 ? 1 : -1, ramp: i % 3 === 0 }); }
function drawFarBank(t, shower, crowd) {
  blit(LOWER.c);
  for (const d of DINERS) if (d.rank >= crowd) { const s = SHOPWIN[d.win]; ctx.drawImage(LOWER.bare[d.win], d.x - s.x, d.y - s.y, 4, 5, d.x, d.y, 4, 5); }   // an empty seat
  const s2 = step(t, 480), staff = crowd > 0;
  // inside: a waiter crossing the izakaya, glasses raised, the sushi cook at work, the takoyaki cook turning his batch
  // (at crowd 0 the shops are lit and empty; the takoyaki shop's griddle steams on)
  if (staff) {
    const wx0 = 296 + Math.round((1 - Math.abs(2 * frac(t / 16) - 1)) * 20);
    rect(ctx, wx0, 141, 2, 2, '#241418'); rect(ctx, wx0 - 1, 143, 4, 3, '#f4f0e8');
  }
  for (let k = 0; k < 4; k++) if (DINERS[k].rank < crowd && hash(k, s2, 61) < 0.12) rect(ctx, 299 + k * 6 + (k > 1 ? 1 : 0), 144, 1, 2, '#fff2b0');
  if (staff) {
    const cx0 = 358 + Math.round(wave(t, 10, 0.2) * 5 + wave(t, 27, 0.5) * 2);
    rect(ctx, cx0, 144, 3, 2, '#f8f8f8'); rect(ctx, cx0, 146, 3, 2, SKIN[1]); rect(ctx, cx0 - 1, 148, 5, 4, '#f4f0e8'); rect(ctx, cx0 + (step(t, 960) & 1 ? 3 : -2), 151, 2, 1, SKIN[1]);
    const tx0 = 404 + Math.round(wave(t, 14, 0.4) * 3);
    rect(ctx, tx0, 141, 3, 1, '#e8342c'); rect(ctx, tx0, 142, 3, 2, SKIN[0]); rect(ctx, tx0 - 1, 144, 5, 7, '#2a2a3a'); rect(ctx, tx0 + (step(t, 1200) & 1 ? 4 : -2), 149, 1, 2, SKIN[0]);
  }
  for (let k = 0; k < 4; k++) {
    const { u } = cycle(t, 44 + k * 7, k * 0.23), rr = 1 + Math.floor(u * 2.2);
    ctx.globalAlpha = 0.45 * Math.sin(Math.PI * u);
    ctx.drawImage(PUFFS[rr], 414 + k * 3 + Math.round(wave(t, 20 + k * 4, k * 0.3) * 2 * u) - rr, 148 - Math.round(u * 16) - rr);
  }
  ctx.globalAlpha = 1;
  halo(HL.warm, 308, 147, 0.55); halo(HL.warm, 362, 148, 0.45); halo(HL.warm, 408, 146, 0.65 + 0.08 * wave(t, 800)); halo(HL.pink, 449, 147, 0.6 + 0.1 * wave(t, 600, 0.3)); halo(HL.vend, 473, 150, 0.8);
  // people on the river walk; every third one takes the ramp up to the bridge
  const fr = Math.floor(t * 4);
  for (const w of FARW) {
    if (w.rank >= crowd) continue;
    const u0 = frac(w.k * t / LOOP + w.ph), u = w.dir > 0 ? u0 : 1 - u0;
    let x, y = FWALK - 12;
    if (w.ramp) { x = Math.round(206 + u * 290); if (x <= 288) y = rampY(x) - 12; }
    else x = Math.round(140 + u * 356);
    ctx.drawImage(TINY[w.st][(fr + w.id) & 1][w.dir > 0 ? 0 : 1], x - 3, y);
    if (shower) drawUmb(x, y - 2, w.id + 80, umbOpen(t, w.id + 80), false);
  }
  // three rows of paper lanterns swaying on their wires
  for (let k = 0; k < 3; k++) ctx.drawImage(LOWER.row, RACK.x0 + Math.round(wave(t, 14 + k * 5, k * 0.31) * 0.9), RACK.y + 1 + k * 4);
  const sd = step(t, 960);
  for (let k = 0; k < 5; k++) {
    halo(HL.lanterns, 311 + k * 38, RACK.y + 6, 0.8 + 0.12 * wave(t, 300 + k * 37, k * 0.21));
    if (hash(k, sd, 66) < 0.3) rect(ctx, RACK.x0 + 5 + Math.floor(hash(k, sd, 67) * 44) * 4, RACK.y + 1 + (k % 3) * 4, 2, 3, '#b86a30');   // one gutters
  }
}

/* ================= Boats: the yellow tour boats, and the festival's drum boat ================= */
const TB = { w: 78, h: 20, keel: 18 }, DB = { w: 120, h: 27, keel: 25 };
// the tour boat's 18 passengers are painted into its sprite (near row: ranks gold(0..8), far row: gold(9..17)), so
// there is one sprite per head count: TOUR_SPR[m] carries the m passengers with the lowest ranks
const TOUR_CUTS = Array.from({ length: 18 }, (_, i) => gold(i)).sort((a, b) => a - b);
/** the boat with the passengers whose rank is below cut; the skipper is always at the engine, the guide stands at the
    bow unless guide is false (crowd 0: the boat sails empty) */
function renderTourBoat(cut = 1, guide = true) {
  const p = pix(TB.w, TB.h), r = rng(301), seats = [];
  for (let x = 0; x < TB.w; x++) {                      // a low open hull, yellow, pointed at the bow
    const bot = x < 4 ? 18 - (4 - x) : x > 64 ? 18 - Math.round((x - 64) * 0.5) : 18;
    for (let y = 12; y <= bot; y++) p.set(x, y, y === 12 ? '#fff2a8' : y === bot && bot === 18 ? '#6a1a1a' : y < 16 ? '#ffd020' : y === 16 ? '#e0a810' : '#b07a10');
    if (x % 3 === 1 && x < 70) p.set(x, 11, x % 2 ? '#fff0b0' : '#ffb040');              // a string of bulbs along the gunwale
    if (x % 12 === 6 && x < 64) p.fill(x, 14, 2, 2, '#2a2a3a');                         // fenders
  }
  glyphP(p, G5[3], 50, 13, '#9a6a00');
  p.fill(0, 8, 4, 4, '#3a3a4a'); p.fill(1, 2, 1, 7, '#8a8aa0');                         // engine box, flag staff
  for (let k = 0; k < 9; k++) {                         // far-side heads, then the near row of passengers
    const fx = 11 + k * 6, nx = 8 + k * 6, st = STY[Math.floor(r() * 13)], st2 = STY[Math.floor(r() * 13)];
    if (gold(9 + k) < cut) { p.fill(fx, 4, 2, 1, HAIR[st2.hr]); p.fill(fx, 5, 2, 2, sh(SKIN[st2.sk], 0.8)); p.fill(fx - 1, 7, 4, 4, sh(st2.yuk || st2.c, 0.6)); }
    if (gold(k) < cut) {
      p.fill(nx, 5, 3, 1, HAIR[st.hr]); p.fill(nx, 6, 3, 2, SKIN[st.sk]); p.fill(nx - 1, 8, 5, 4, st.yuk || st.c); p.fill(nx - 1, 8, 1, 4, sh(st.yuk || st.c, 0.7));
      if (st.hat || st.cap) p.fill(nx, 5, 3, 1, st.hat || st.cap);
    }
    seats.push({ x: nx, sk: SKIN[st.sk], wave: r() < 0.55, rank: gold(k) });
  }
  if (guide) { p.fill(63, 0, 3, 1, '#ffd020'); p.fill(63, 1, 3, 2, SKIN[0]); p.fill(62, 3, 5, 5, '#2a5ac8'); p.fill(63, 3, 3, 5, '#f4f4f8'); p.fill(63, 8, 1, 4, '#22263a'); p.fill(65, 8, 1, 4, '#22263a'); }   // the guide
  p.fill(5, 6, 3, 1, '#f4f4f8'); p.fill(5, 7, 3, 2, SKIN[1]); p.fill(4, 9, 5, 3, '#22263a');                                                              // the skipper
  return { c: p.done(), seats };
}
const PADDLERS = [14, 24, 34, 44, 84, 94, 104], DB_LANTERNS = [8, 29, 52, 79, 99, 114];
function renderDrumBoat() {
  const p = pix(DB.w, DB.h);
  for (let x = 0; x < DB.w; x++) {                      // a long wooden hull, both ends turned up, a red and white band
    const top = x < 10 ? 20 - Math.round((10 - x) * 0.5) : x > 108 ? 20 - Math.round((x - 108) * 0.6) : 20;
    for (let y = top; y <= 25; y++) p.set(x, y, y === top ? '#d8a060' : y === top + 1 ? ((x >> 2) & 1 ? '#e8342c' : '#f4f0e4') : y < 24 ? '#8a5a2a' : '#5a3618');
  }
  for (const x of DB_LANTERNS) { p.fill(x, 6, 1, 14, '#4a3420'); p.fill(x - 2, 0, 5, 6, '#ff4a3a'); p.fill(x - 2, 2, 5, 2, '#fff0d0'); p.fill(x - 1, 6, 3, 1, '#2a1a10'); }
  for (const x of PADDLERS) {                           // paddlers in white happi and headbands
    p.fill(x, 9, 3, 1, '#f4f0e4'); p.fill(x, 10, 3, 2, SKIN[1]); p.fill(x - 1, 12, 5, 7, '#f0ece4'); p.fill(x - 1, 12, 1, 7, '#c8c4d0'); p.fill(x + 1, 12, 1, 4, '#2a4ab0'); p.fill(x - 1, 17, 5, 1, '#2a4ab0');
  }
  p.fill(57, 4, 3, 1, '#f4f0e4'); p.fill(57, 5, 3, 2, SKIN[1]); p.fill(56, 7, 5, 7, '#2a4ab0'); p.fill(57, 9, 3, 1, '#f4f0e4'); p.fill(57, 14, 1, 6, '#f0ece4'); p.fill(59, 14, 1, 6, '#f0ece4');   // the drummer
  for (let x = 64; x <= 75; x++) for (let y = 7; y <= 16; y++) {                                 // the big taiko on its stand
    const e = ((x - 69.5) / 6.4) ** 2 + ((y - 11.5) / 5.4) ** 2;
    if (e < 1) p.set(x, y, x === 64 || x === 75 || e > 0.8 ? '#f4e8c8' : (x & 1) ? '#8a2a1a' : '#a8381e');
  }
  p.fill(66, 17, 1, 3, '#3a2414'); p.fill(73, 17, 1, 3, '#3a2414');
  return p.done();
}
/** where a boat is, u in 0..1 of its pass heading west (dir 1); heading east it runs the same path backwards.
    First it comes up the canal from the vanishing point, growing; then it crosses in front of us at full size. */
function boatAt(u, dir, w, farShare) {
  const v = dir > 0 ? u : 1 - u;
  if (v < farShare) { const a = v / farShare, e = Math.pow(a, 1.7); return { x: lerp(VP.x + 4, BR.ab - 4, e), y: lerp(VP.y + 1, LANE, e), s: lerp(0.1, 1, e) }; }
  return { x: lerp(BR.ab - 4, W + w / 2 + 6, (v - farShare) / (1 - farShare)), y: LANE, s: 1 };
}
function drawBoat(spr, b, dir, B, t, seed) {
  const s = b.s, w = Math.round(B.w * s), h = Math.round(B.h * s), x0 = Math.round(b.x - w / 2), y0 = Math.round(b.y - B.keel * s);
  const img = dir > 0 ? spr[0] : spr[1];
  ctx.globalAlpha = 0.3; ctx.drawImage(dir > 0 ? spr[2] : spr[3], 0, B.h - 1 - B.keel, B.w, 9, x0, y0 + Math.round(B.keel * s) + 1, w, Math.max(1, Math.round(9 * s)));   // its mirror image
  ctx.globalAlpha = 1;
  ctx.drawImage(img, x0, y0, w, h);
  if (s < 0.5) return null;
  const stern = dir > 0 ? x0 : x0 + w, st = step(t, 1440);  // wake and bow wave
  ctx.fillStyle = '#dfeaff';
  for (let j = 0; j < 16; j++) { ctx.globalAlpha = 0.6 * (1 - j / 16) * (0.5 + 0.5 * hash(j, st, seed)); ctx.fillRect(stern - dir * (2 + j * 3 + Math.floor(hash(j, st, seed + 1) * 3)), b.y + ((j + st) & 1), 2, 1); }
  ctx.globalAlpha = 0.8; ctx.fillRect(dir > 0 ? x0 + w - 2 : x0 - 1, b.y, 3, 1);
  ctx.globalAlpha = 1;
  return { x0, y0, w, bx: lx => (dir > 0 ? x0 + lx : x0 + w - 1 - lx) };
}
function drawBoats(t, crowd) {
  let aboard = 0;
  while (aboard < TOUR_CUTS.length && TOUR_CUTS[aboard] < crowd) aboard++;      // how many passengers the tour boats carry
  const crews = crowd > 0;                             // crowd 0: the tour boats sail empty (only the painted skipper), the paddled drum boat stays away
  for (let i = 0; i < TOURS.length; i++) {
    const B = TOURS[i];
    if (t < B.a || t >= B.b) continue;
    const b = boatAt((t - B.a) / (B.b - B.a), B.dir, TB.w, 0.28), g = drawBoat(crews ? TOUR_SPR[aboard] : TOUR_BARE, b, B.dir, TB, t, 40 + i);
    if (!g) continue;
    const ws = Math.floor(t * 5), fl = Math.floor(t * 6) & 1;
    TOUR.seats.forEach((sd, k) => { if (sd.wave && sd.rank < crowd && hash(k, i, 44) < 0.7) rect(ctx, g.bx(sd.x + 3) , g.y0 + 2 + ((ws + k) & 1), 1, 4, sd.sk); });   // passengers wave
    if (crews) rect(ctx, g.bx(67) + ((ws & 1) ? B.dir : 0), g.y0 - 2, 1, 5, SKIN[0]);                                           // so does the guide
    for (let j = 0; j < 4; j++) rect(ctx, g.bx(1) - B.dir * (1 + j), g.y0 + 2 + ((j + fl) & 1), 1, 3, j & 1 ? '#f4f4f8' : '#e8342c');   // the flag
    halo(HL.boat, g.x0 + g.w / 2, LANE - 8, 0.8);
  }
  if (crews && t >= DONDOKO.far && t < DONDOKO.b) {
    const far = t < DONDOKO.a, u = far ? (t - DONDOKO.far) / (DONDOKO.a - DONDOKO.far) * 0.25 : 0.25 + 0.75 * (t - DONDOKO.a) / (DONDOKO.b - DONDOKO.a);
    const b = boatAt(u, 1, DB.w, 0.25), g = drawBoat(DRUM_SPR, b, 1, DB, t, 60);
    halo(HL.festival, b.x, b.y - 14 * b.s, Math.min(1, 0.35 + b.s) * (0.85 + 0.15 * wave(t, 480)));
    if (!g) return;
    const ph = Math.floor(t * 3) & 1, beat = Math.floor(t * 6) & 1;
    ctx.fillStyle = '#e8d0a0';
    for (const px0 of PADDLERS) for (let j = 0; j < 9; j++) ctx.fillRect(g.x0 + px0 + 3 + Math.round((ph ? -0.5 : 0.45) * j), g.y0 + 15 + j, 1, 1);   // paddles, all together
    ctx.fillStyle = '#ffffff';
    if (ph) for (const px0 of PADDLERS) ctx.fillRect(g.x0 + px0 - 2, LANE - 1, 2, 1);
    rect(ctx, g.x0 + 61, g.y0 + (beat ? 3 : 8), 1, 4, SKIN[1]); rect(ctx, g.x0 + 62 + (beat ? 0 : 1), g.y0 + (beat ? 1 : 9), 1, 3, '#f4e8c8');     // the drummer's sticks
    rect(ctx, g.x0 + 55, g.y0 + (beat ? 8 : 3), 1, 4, SKIN[1]);
    for (const lx of [8, 52, 99]) halo(HL.lantern, g.x0 + lx, g.y0 + 3, 0.9);
  }
}

/* ================= The near boardwalk: boards, the rail, the lantern rack, the takoyaki stall, a bench ================= */
const BENCH = { x0: 138, x1: 172, seat: 224, feet: 232 }, CRATE = { x: 178, y: 225 };
const KIT = { x: 26, y: 228, w: 56, h: 19 };          // the boards under the busker's amp and guitar case
const RACK_L = [[4, 150], [13, 150], [22, 150], [4, 166], [13, 166], [22, 166], [4, 182], [13, 182], [22, 182]];   // the near rack's lanterns (left edge, top)
const PLANKS = [208, 212, 217, 223, 230, 238, 247, 257, 268];
const POOLS = [[432, 240, 84, 26, '#ffb060', 0.5], [18, 224, 50, 20, '#ffc070', 0.42], [236, 226, 60, 16, '#ff6a40', 0.2], [300, 230, 50, 16, '#40c8ff', 0.16], [356, 224, 40, 14, '#ffe0a0', 0.14], [120, 236, 70, 20, '#c040e0', 0.14]];
function renderBoards() {
  const p = pix(W, H), r = rng(55);
  for (let y = BOARD; y < H; y++) {
    const row = PLANKS.findIndex(q => q > y) - 1, seam = PLANKS.includes(y), jo = 37 + ((row * 53) % 41);
    for (let x = 0; x < W; x++) {
      let base = mixc('#2a2026', '#3a2c2e', hash(x >> 3, row, 9) * 0.8 + (hash(x, y, 8) - 0.5) * 0.3);
      if (seam) base = C('#1a1218'); else if ((x + row * 29) % jo === 0) base = C('#1f161c');
      let rr = base[0], gg = base[1], bb = base[2];
      for (const [cx, cy, rx, ry, col, a] of POOLS) {    // light lying on the boards
        const d = Math.hypot((x - cx) / rx, (y - cy) / ry);
        if (d >= 1) continue;
        const k = Math.floor(Math.pow(1 - d, 1.4) * 5 + bayer(x, y)) / 5 * a, c = C(col);
        rr += c[0] * k; gg += c[1] * k; bb += c[2] * k;
      }
      p.set(x, y, [Math.min(255, rr | 0), Math.min(255, gg | 0), Math.min(255, bb | 0)]);
    }
  }
  for (let x = 0; x < W; x++) { p.set(x, BOARD, '#6a5a64'); p.set(x, BOARD + 1, '#16101a'); }      // the quay's edge
  // the rail: posts, a bright hand rail, a lower bar
  for (let x = 0; x < W; x++) { p.set(x, RAIL, '#a8a4cc'); p.set(x, RAIL + 1, '#5a5678'); p.set(x, RAIL + 5, '#48445f'); }
  for (let x = 12; x < W; x += 30) { p.fill(x, RAIL - 1, 2, BOARD - RAIL + 2, '#3a3654'); p.fill(x, RAIL - 1, 1, BOARD - RAIL + 2, '#7a76a0'); p.fill(x - 1, RAIL - 2, 4, 1, '#8a86b0'); }
  // the near lantern rack at the frame's left edge
  p.fill(31, 146, 2, BOARD - 144, '#5a3a24'); p.fill(31, 146, 1, BOARD - 144, '#8a5e3a'); p.fill(0, 146, 35, 2, '#5a3a24'); p.fill(0, 146, 35, 1, '#8a5e3a');
  for (const y of [162, 178]) p.fill(0, y, 32, 1, '#3a2618');
  // the bench, the crate, the busker's amp and open guitar case
  p.fill(BENCH.x0, BENCH.seat + 1, BENCH.x1 - BENCH.x0, 2, '#8a6a50'); p.fill(BENCH.x0, BENCH.seat + 1, BENCH.x1 - BENCH.x0, 1, '#b08a68');
  for (const x of [BENCH.x0 + 2, BENCH.x1 - 4]) p.fill(x, BENCH.seat + 3, 2, BENCH.feet - BENCH.seat - 2, '#4a3828');
  for (let y = 0; y < 8; y++) for (let i = 0; i < 12; i++) p.set(CRATE.x + i, CRATE.y + y, y === 0 ? '#c89a5a' : i % 4 === 0 || y === 4 ? '#5a3c20' : '#9a7040');
  // the busker's things are painted in; the boards under them are kept, to be laid over them when nobody plays (crowd 0)
  const bare = pix(KIT.w, KIT.h);
  for (let y = 0; y < KIT.h; y++) for (let x = 0; x < KIT.w; x++) { const o = ((KIT.y + y) * W + KIT.x + x) * 4; bare.set(x, y, [p.d[o], p.d[o + 1], p.d[o + 2]]); }
  p.fill(26, 228, 10, 11, '#1a1820'); p.fill(27, 229, 8, 7, '#2e2a3a'); for (let y = 230; y < 236; y += 2) p.fill(28, y, 6, 1, '#16141c'); p.fill(26, 228, 10, 1, '#4a4660');
  for (let y = 0; y < 6; y++) for (let i = 0; i < 22; i++) { const e = y === 0 || y === 5 || i === 0 || i === 21; p.set(58 + i + (y >> 1), 241 + y, e ? '#2a1c18' : (i + y) % 5 === 0 ? '#a8242c' : '#7a1820'); }
  return { c: p.done(), bare: bare.done() };
}
function renderLantern(big) {                           // a festival lantern: cream paper, a red band, an invented glyph
  const w = big ? 7 : 5, h = big ? 11 : 9, p = pix(w, h), c = (w - 1) / 2;
  for (let y = 1; y < h - 1; y++) {
    const hw = c * Math.sqrt(Math.max(0, 1 - (((y - 0.5) / (h - 2)) * 2 - 1) ** 2 * 0.55)) + 0.4;
    for (let i = 0; i < w; i++) if (Math.abs(i - c) <= hw) p.set(i, y, y === 2 || y === h - 3 ? '#d83a2c' : pick(['#e8a050', '#ffd088', '#fff0c4', '#fffbe8'], 1 - Math.abs(i - c) / (hw + 0.6) - (y & 1) * 0.12, i, y));
  }
  p.fill(c - 1, 0, 3, 1, '#2a1a14'); p.fill(c - 1, h - 1, 3, 1, '#2a1a14');
  if (big) glyphP(p, ['.#.', '###', '.#.', '#.#'], 2, 4, '#7a1420'); else p.fill(c, 4, 1, 2, '#7a1420');
  return p.done();
}
function renderStall() {
  const x0 = STALL.x, w = W - x0, back = pix(w, 50), front = pix(w, 50), Y = y => y - 190;
  // the fascia: an octopus, TAKO in cream, three glyphs, the price
  back.fill(0, Y(192), w, 12, '#7a1420'); back.fill(0, Y(192), w, 1, '#ffd870'); back.fill(0, Y(203), w, 1, '#3a0810');
  back.ell(8, Y(197), 4, 3.4, '#ff5a48'); back.fill(6, Y(196), 1, 2, '#ffffff'); back.fill(9, Y(196), 1, 2, '#ffffff'); for (const i of [4, 6, 8, 10, 12]) back.fill(i, Y(201), 1, 2, '#ff5a48');
  back.txt('TAKO', 17, Y(193), '#3a0810', 2); back.txt('TAKO', 16, Y(193), '#fff2d8', 2);
  [0, 5, 3].forEach((g, k) => glyphP(back, G5[g], 51 + k * 7, Y(195), '#ffd23a'));
  back.txt('500', 74, Y(195), '#ffffff');
  for (let i = 0; i < w; i++) for (let y = 204; y <= 208; y++) if (y < 208 || (i % 8) < 6) back.set(i, Y(y), (i >> 2) & 1 ? '#f4f0e4' : '#d8342c');   // striped valance
  for (let y = 209; y < STALL.counter; y++) for (let i = 2; i < w; i++) back.set(i, Y(y), pick(['#2a1610', '#4a2616', '#6e3a1c', '#8e4e24'], 0.75 - (y - 209) / 22 - Math.abs(i - 46) / 120, i, y));
  for (let i = 6; i < w; i += 9) { back.fill(i, Y(212), 3, 5, ['#e8e0d0', '#c83a3a', '#f0c040', '#3a8a5a'][(i / 9 | 0) & 3]); back.fill(i - 1, Y(217), 5, 1, '#3a2014'); }   // jars on the back shelf
  back.fill(0, Y(204), 2, 36, '#5a3a24'); back.fill(0, Y(204), 1, 36, '#8a5e3a');
  // the counter: the iron griddle with its rows of balls, the plank front, a cloth menu
  front.fill(0, Y(STALL.counter), w, 2, '#d8a468'); front.fill(0, Y(STALL.counter) + 2, w, 1, '#5a3418');
  front.fill(26, Y(STALL.counter) - 2, 40, 3, '#22202a'); for (let i = 28; i < 65; i += 3) { front.fill(i, Y(STALL.counter) - 2, 2, 2, (i / 3 | 0) % 4 === 0 ? '#f0c060' : '#c8842c'); }
  for (let y = STALL.counter + 3; y <= STALL.feet; y++) for (let i = 0; i < w; i++) front.set(i, Y(y), i % 11 === 0 ? '#3a1e10' : pick(['#4e2a16', '#6a3a1e', '#7e4826'], 0.8 - (y - STALL.counter) / 20 + hash(i >> 2, 3, 6) * 0.3, i, y));
  front.fill(10, Y(228), 24, 9, '#f4f0e4'); front.fill(10, Y(228), 24, 1, '#c8c0b0'); [2, 6, 4].forEach((g, k) => glyphP(front, G5[g], 12 + k * 7, Y(230), '#c8242c'));
  front.fill(44, Y(229), 30, 7, '#1a1218'); front.txt('8 PCS', 47, Y(230), '#ffd23a');
  return { back: back.done(), front: front.done() };
}

/* ================= Per-frame: the people on the near boardwalk ================= */
const put = (spr, x, fy) => ctx.drawImage(spr, x - PCX, fy - spr.height + 1);
const TOP = (fy, S) => fy - (DIM[S][0] + DIM[S][1] + DIM[S][2]) + 1;          // the row of the top of the head
const SLEEVE = {}, sleeve = st => SLEEVE[st.yuk || st.c] || (SLEEVE[st.yuk || st.c] = css(sh(st.yuk || st.c, 0.84)));
/** a hanging arm for a back view built without arms: column x, shoulder row ty */
function armDown(x, ty, st, tH) {
  const yk = st.yuk ? 5 : 2;
  rect(ctx, x, ty, 1, yk, sleeve(st)); rect(ctx, x, ty + yk, 1, tH - yk - 1, st.yuk ? sleeve(st) : SKIN[st.sk]); rect(ctx, x, ty + tH - 1, 1, 1, SKIN[st.sk]);
}
// the stream of walkers: [style, size, crossings per loop, phase, direction, feet row]; pair: walks with the one before
// (one unit, one rank)
const WK = [
  { st: 3, S: 0, k: 5, ph: 0.05, dir: 1, fy: 216 }, { st: 5, S: 0, k: 5, ph: 0.066, dir: 1, fy: 217, pair: 1 },        // a couple in yukata and jinbei
  { st: 0, S: 0, k: 8, ph: 0.42, dir: -1, fy: 218 }, { st: 6, S: 0, k: 7, ph: 0.74, dir: 1, fy: 220 },
  { st: 9, S: 1, k: 7, ph: 0.2, dir: -1, fy: 223 }, { st: 16, S: 'k', k: 7, ph: 0.214, dir: -1, fy: 224, pair: 1 },     // a father and his boy
  { st: 8, S: 1, k: 9, ph: 0.55, dir: 1, fy: 227 }, { st: 12, S: 1, k: 6, ph: 0.36, dir: 1, fy: 229 },
  { st: 15, S: 'k', k: 15, ph: 0.1, dir: 1, fy: 241, fps: 8 },                                               // a girl running ahead
  { st: 2, S: 1, k: 8, ph: 0.9, dir: -1, fy: 243 }, { st: 4, S: 1, k: 5, ph: 0.61, dir: -1, fy: 245, lant: 1 },
  { st: 7, S: 2, k: 6, ph: 0.3, dir: 1, fy: 248 }, { st: 10, S: 2, k: 7, ph: 0.02, dir: -1, fy: 252 },
  { st: 1, S: 2, k: 8, ph: 0.47, dir: 1, fy: 256, dog: 1 }, { st: 11, S: 2, k: 6, ph: 0.82, dir: -1, fy: 262 },
  { st: 17, S: 1, k: 6, ph: 0.15, dir: -1, fy: 232 }, { st: 18, S: 2, k: 5, ph: 0.5, dir: 1, fy: 250 }, { st: 19, S: 2, k: 7, ph: 0.68, dir: 1, fy: 259 },
  { st: 20, S: 2, k: 7, ph: 0.12, dir: -1, fy: 265 }, { st: 16, S: 'k', k: 13, ph: 0.6, dir: -1, fy: 254, fps: 8 },
];
{ let unit = -1; WK.forEach((w, i) => { w.id = i; if (!w.pair) unit++; w.rank = gold(unit); }); }   // 18 units: the dog and the lantern go with their walkers
const DOG_ROWS = [['.......e.', '......ddd', 'tddddddd.', '.dddddd..', '.d....d..'], ['.......e.', '......ddd', 'tddddddd.', '.dddddd..', '..d..d...']];
function drawWalker(w, t, shower) {
  const u = frac(w.k * t / LOOP + w.ph), x = Math.round(w.dir > 0 ? -14 + u * 508 : 494 - u * 508), f = Math.floor(t * (w.fps || 5) + w.id * 1.3) & 3;
  put(w.spr[f], x, w.fy);
  const top = TOP(w.fy, w.S);
  if (w.dog) {                                         // a shiba on a red lead
    const dx = x + w.dir * 13;
    ctx.drawImage(DOG[f & 1][w.dir > 0 ? 0 : 1], dx - 4, w.fy - 4);
    ctx.fillStyle = '#c8342c';
    for (let j = 0; j <= 8; j++) ctx.fillRect(Math.round(lerp(x + w.dir * 2, dx + w.dir * 2, j / 8)), Math.round(lerp(w.fy - 11, w.fy - 3, j / 8)), 1, 1);
  }
  if (w.lant) {                                        // she carries a paper lantern on a stick
    const lx = x + w.dir * 6;
    rect(ctx, x + w.dir * 2, top + 9, 1, 1, '#6a4a2a'); rect(ctx, lx - (w.dir > 0 ? 3 : 0), top + 8, 4, 1, '#6a4a2a');
    ctx.drawImage(LANT_S, lx - 2, top + 9 + (f & 1)); halo(HL.lantern, lx, top + 13, 0.8);
  }
  if (shower && w.S !== 'k') drawUmb(x + w.dir, top - 3, w.id, umbOpen(t, 100 + w.id), true);
}
/* ---- the queue at the takoyaki stall: every 20 s the first is served and walks off eating, the others shuffle up, a new one joins ---- */
const QS = [0, 3, 9, 8, 1, 12, 6, 2, 11, 4, 7, 5], QX = [382, 370, 358], QFY = 236;
function drawQueue(t, bz, shower, crowd) {
  const c = Math.floor(t / 20) % 12, u = t % 20, f = Math.floor(t * 5) & 3, mv = smooth(3, 6, u);
  const n = RANK.queue3 < crowd ? 3 : 2;               // the queue's places: a thinner crowd drops the last one, and the newcomer joins a place earlier
  const tray = (x, y) => { rect(ctx, x, y, 5, 2, '#ecdcb4'); rect(ctx, x + 1, y - 1, 1, 1, '#b8702a'); rect(ctx, x + 3, y - 1, 1, 1, '#b8702a'); };
  for (const [cc, uu] of [[c + 11, u + 20], [c, u]]) {   // those already served: walking off to the left, eating
    const x = Math.round(QX[0] - (uu - 2.4) * 14);
    if (uu < 2.4 || x < -14) continue;
    const y = Math.round(lerp(QFY, 240, smooth(2.4, 8, uu)));
    put(QSPR[cc % 12].l[f], x, y); tray(x - 7, y - 12 - (frac(uu / 3) > 0.8 ? 3 : 0));
    if (shower) drawUmb(x - 1, TOP(y, 1) - 3, cc, umbOpen(t, 300 + cc % 12), true);
  }
  for (let j = 0; j < n; j++) {                        // the three waiting (two in a thinner crowd)
    if (j === 0 && u >= 2.4) continue;
    const x = j === 0 ? QX[0] : Math.round(lerp(QX[j], QX[j - 1], mv)), moving = j > 0 && mv > 0 && mv < 1;
    put(QSPR[(c + j) % 12].r[moving ? f : 1], x, QFY);
    if (j === 0 && u > 1.2) { rect(ctx, x + 3, QFY - 13, 3, 1, SKIN[STY[QS[c]].sk]); tray(x + 5, QFY - 14); }       // reaching for the tray, taking it
    else if (!moving && hash(c + j, step(t, 240), 88) < 0.25) rect(ctx, x + 3, QFY - 14, 1, 2, '#cfe8ff');         // a glance at a phone
    if (shower) drawUmb(x + 1, TOP(QFY, 1) - 3, c + j + 3, umbOpen(t, 320 + (c + j) % 12), true);
  }
  if (u >= 6) {                                        // the newcomer comes round the front of the stall and joins the end
    const a = clamp((u - 6) / 10, 0, 1), x = Math.round(lerp(494, QX[n - 1], a)), y = Math.round(lerp(244, QFY, smooth(0.6, 1, a)));
    put(QSPR[(c + n) % 12][a < 1 ? 'l' : 'r'][a < 1 ? f : 1], x, y);
    if (shower) drawUmb(x, TOP(y, 1) - 3, c + n + 3, umbOpen(t, 320 + (c + n) % 12), true);
  }
}
/* ---- the people along the rail, backs to us: friends taking a selfie, a photographer, a couple in yukata ---- */
function drawRailPeople(t, bz, shower, crowd) {         // six units, each there while its RANK is below the crowd share
  const s = step(t, 1200), fl = Math.floor(t * 6) & 1, selfie = frac(t / 15), pt = Math.floor(t * 4) & 1;
  const ty = TOP(213, 0) + 4, hy = TOP(214, 0) + 4, my = TOP(214, 1) + 5, dy = TOP(214, 1);
  if (RANK.selfie < crowd) {
    put(ACT.teenA, 120, 213); put(ACT.teenB, 132, 213);
    armDown(116, ty, STY[6], 7);
    rect(ctx, 123, ty - 2, 1, 3, sleeve(STY[6])); rect(ctx, 124, ty - 5, 1, 4, SKIN[0]); rect(ctx, 124, ty - 8, 3, 4, '#1a1a22'); rect(ctx, 125, ty - 7, 1, 2, '#bfe4ff');   // the phone at arm's length
    if (selfie > 0.9) rect(ctx, 136, ty - 4, 1, 5, SKIN[0]);                                     // a peace sign for the camera
    if (selfie < 0.012) { rect(ctx, 124, ty - 9, 3, 3, '#ffffff'); halo(HL.flash, 125, ty - 8, 1); }
  }
  if (RANK.photo < crowd) {
    put(ACT.photo, 252, 213);
    if (bz > 0.25) {                                   // the photographer's camera goes up whenever something passes
      rect(ctx, 248, ty - 4, 1, 5, SKIN[1]); rect(ctx, 255, ty - 4, 1, 5, SKIN[1]); rect(ctx, 249, ty - 7, 6, 3, '#22222c'); rect(ctx, 251, ty - 6, 2, 1, '#8ab0e0');
      if (hash(3, s, 77) < 0.03 + bz * 0.06) { rect(ctx, 250, ty - 9, 4, 2, '#ffffff'); halo(HL.flash, 252, ty - 8, 1); }
    } else { armDown(248, ty, STY[9], 7); armDown(255, ty, STY[9], 6); rect(ctx, 253, ty + 5, 4, 3, '#22222c'); }
  }
  if (RANK.couple < crowd) {
    put(ACT.her, 306, 214); put(ACT.him, 318, 214);
    armDown(302, hy, STY[12], 7);
    rect(ctx, 309, hy - 1, 1, 3, sleeve(STY[12])); rect(ctx, 310, hy - 3, 1, 3, SKIN[0]);         // she fans herself
    rect(ctx, 310 + fl, hy - 7, 3, 4, '#f4f0f0'); rect(ctx, 311 + fl, hy - 6, 1, 2, '#ff6a8a');
    armDown(314, my, STY[5], 8);
    if (bz > 0.3) { rect(ctx, 321, my - 1, 1, 2, sleeve(STY[5])); rect(ctx, 322, my - 3 - pt, 1, 3, SKIN[1]); rect(ctx, 323, my - 5 - pt, 1, 3, SKIN[1]); }   // he points out the boats
    else armDown(321, my, STY[5], 8);
  }
  // a father with his boy on his shoulders (the boy waves at every boat), two friends, a traveller filming
  if (RANK.dadSon < crowd) {
    put(ACT.dad, 66, 214);
    ctx.drawImage(ACT.son, 0, 0, PW, 9, 66 - PCX, dy - 9, PW, 9);
    rect(ctx, 62, dy + 2, 1, 4, SKIN[0]); rect(ctx, 69, dy + 2, 1, 4, SKIN[0]); rect(ctx, 63, dy - 1, 1, 3, SKIN[1]); rect(ctx, 68, dy - 1, 1, 3, SKIN[1]);
    if (bz > 0.2) rect(ctx, 70 + pt, dy - 12, 1, 4, SKIN[1]); else rect(ctx, 69, dy - 5, 1, 3, SKIN[1]);
  }
  if (RANK.friends < crowd) {
    put(ACT.pairA, 176, 213); put(ACT.pairB, 187, 213);
    armDown(191, ty, STY[20], 7);
    if (bz > 0.3) { rect(ctx, 182, ty - 1, 1, 2, sleeve(STY[20])); rect(ctx, 181, ty - 3 - pt, 1, 3, SKIN[0]); } else armDown(183, ty, STY[20], 7);
  }
  if (RANK.trav < crowd) {
    put(ACT.trav, 352, 214);
    if (bz > 0.25) {
      rect(ctx, 348, dy + 2, 1, 4, SKIN[2]); rect(ctx, 355, dy + 2, 1, 4, SKIN[2]); rect(ctx, 350, dy - 1, 5, 3, '#1a1a22'); rect(ctx, 351, dy, 3, 1, '#bfe4ff');
      if (hash(9, s, 79) < 0.02 + bz * 0.05) { rect(ctx, 351, dy - 3, 3, 2, '#ffffff'); halo(HL.flash, 352, dy - 2, 1); }
    } else { armDown(348, dy + 5, STY[19], 8); armDown(355, dy + 5, STY[19], 8); }
  }
  if (shower) {                                        // each unit's umbrella
    if (RANK.selfie < crowd) drawUmb(126, TOP(213, 0) - 3, 3, umbOpen(t, 203), true);
    if (RANK.couple < crowd) drawUmb(312, TOP(214, 1) - 3, 1, umbOpen(t, 204), true);
    if (RANK.photo < crowd) drawUmb(252, TOP(213, 0) - 3, 5, umbOpen(t, 205), true);
    if (RANK.friends < crowd) drawUmb(181, TOP(213, 0) - 3, 6, umbOpen(t, 206), true);
    if (RANK.trav < crowd) drawUmb(352, dy - 3, 0, umbOpen(t, 207), true);
    if (RANK.dadSon < crowd) drawUmb(66, dy - 11, 7, umbOpen(t, 208), true);
  }
}
/* ---- a mother watching her girl run about with a goldfish in a bag ---- */
function drawKid(t) {
  put(ACT.mum, 207, 218);
  const u = frac(t / 8), dir = u < 0.5 ? 1 : -1, kx = Math.round(222 + (u < 0.5 ? u * 2 : 2 - u * 2) * 28), f = Math.floor(t * 8) & 3;
  put((dir > 0 ? ACT.kid : ACT.kidL)[f], kx, 221);
  const bx = kx + dir * 3, by = 216 + (f & 1);
  rect(ctx, bx, by - 2, 1, 2, '#e8e8f0'); ctx.globalAlpha = 0.6; rect(ctx, bx - 1, by, 3, 4, '#bfe8ff'); ctx.globalAlpha = 1; rect(ctx, bx - ((f >> 1) & 1), by + 2, 2, 1, '#ff6a20');
}
/* ---- two friends on the bench sharing takoyaki; the cat on its crate, which comes begging twice a loop ---- */
function drawBench(t, bz, shower) {
  [[148, ACT.sitA, 0, STY[0]], [162, ACT.sitB, 2.6, STY[11]]].forEach(([x, spr, off, st]) => {
    const top = BENCH.seat - 14, ty = top + 6, u = frac((t + off) / 5), up = u < 0.12 ? u / 0.12 : u < 0.22 ? 1 : u < 0.34 ? 1 - (u - 0.22) / 0.12 : 0;
    ctx.drawImage(spr, x - PCX, top);
    rect(ctx, x - 3, top + 13, 6, 2, '#ecdcb4'); for (const i of [-2, 0, 2]) rect(ctx, x + i, top + 12, 1, 1, '#b8702a');     // the paper tray on the lap
    armDown(x - 4, ty, st, 7);
    const hx = Math.round(lerp(x + 2, x, up)), hy = Math.round(lerp(top + 11, top + 4, up));    // the other hand: tray to mouth and back
    rect(ctx, x + 3, ty, 1, 3, sleeve(st)); rect(ctx, Math.round((x + 3 + hx) / 2), Math.round((ty + 3 + hy) / 2), 1, 2, SKIN[st.sk]); rect(ctx, hx, hy, 1, 2, SKIN[st.sk]);
    if (up > 0 && up < 1) rect(ctx, hx - 1, hy - 2, 2, 2, '#b8702a');
  });
  if (shower) drawUmb(155, BENCH.seat - 18, 6, umbOpen(t, 210), true);
}
const CAT_LOAF = ['.o.o......', '.owwo.....', 'owwwko....', 'wwokkwwoo.', 'wwwwkkwwwo'], CAT_SIT = ['o.o..', 'www..', 'wkw..', '.wwo.', '.wkkw', '.wwkw', '.wwww'];
const CAT_KEY = { o: '#e88a3a', w: '#f4efe6', k: '#2a2228' };
function drawAlleyCat(t, bz, shower, crowd) {
  const tau = crowd > 0 ? t % 120 : 0;                 // with nobody on the bench (crowd 0) the cat stays on its crate
  const begging = tau >= 40 && tau < 54, hop = tau >= 38 && tau < 40 ? (tau - 38) / 2 : tau >= 54 && tau < 56 ? 1 - (tau - 54) / 2 : begging ? 1 : 0;
  const ts = wave(t, 30, 0.2) * 0.9 + wave(t, 97, 0.5) * 0.4, blink = frac(t / 5) > 0.955;
  ctx.fillStyle = '#f4efe6';
  if (hop <= 0) {                                      // loafing on the crate: the tail swings, the eyes blink
    const X = CRATE.x + 1, Y = CRATE.y - 5;
    CAT_LOAF.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (CAT_KEY[row[i]]) rect(ctx, X + i, Y + j, 1, 1, CAT_KEY[row[i]]); });
    for (let j = 0; j < 4; j++) rect(ctx, X + 10 + Math.round(ts * (j / 3) * 1.5), Y + 4 - j, 1, 1, '#e88a3a');
    if (!blink) { rect(ctx, X + 1, Y + 2, 1, 1, '#c8f060'); rect(ctx, X + 3, Y + 2, 1, 1, '#c8f060'); }
    return;
  }
  const X = Math.round(lerp(CRATE.x + 1, 169, hop)), Y = Math.round(lerp(CRATE.y - 7, BENCH.feet - 6, hop) - Math.sin(Math.PI * hop) * (hop < 1 ? 5 : 0));
  CAT_SIT.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (CAT_KEY[row[i]]) rect(ctx, X + i, Y + j, 1, 1, CAT_KEY[row[i]]); });
  for (let j = 0; j < 3; j++) rect(ctx, X + 5 + j, Y + 6 - Math.round(Math.abs(ts) * j * 0.8), 1, 1, '#e88a3a');
  if (begging && frac(t / 2) < 0.3) rect(ctx, X - 1, Y + 3, 1, 2, '#f4efe6');                    // a paw goes up
}
/* ---- the busker with his guitar, and the ones who stopped to listen (every minute the song ends and they clap) ---- */
const NOTE = ['.##', '.#.', '.#.', '##.', '##.'];
function drawBusker(t) {
  const x = 46, fy = 238, ty = TOP(fy, 2) + 5, playing = (t % 60) < 52, strum = playing ? [0, 1, 2, 1][Math.floor(t * 8) & 3] : 1;
  put(ACT.busker, x, fy);
  ctx.fillStyle = '#6a4020';
  for (let j = 0; j < 10; j++) ctx.fillRect(x + 5 + j, ty + 6 - Math.round(j * 0.6), 1, 1);       // the guitar's neck, then its body
  rect(ctx, x + 14, ty, 2, 2, '#3a2414');
  rect(ctx, x - 2, ty + 5, 8, 5, '#c88a40'); rect(ctx, x - 1, ty + 4, 6, 1, '#d89a50'); rect(ctx, x - 1, ty + 10, 6, 1, '#a86a2c'); rect(ctx, x + 1, ty + 6, 2, 2, '#3a2010');
  rect(ctx, x + 2, ty + 1, 2, 2, sleeve(STY[14])); for (let j = 0; j < 6; j++) rect(ctx, x + 4 + j, ty + 3 - (j >> 1), 1, 1, SKIN[0]);   // the fretting arm
  rect(ctx, x, ty + 1, 1, 3, sleeve(STY[14])); rect(ctx, x + (strum > 1 ? 1 : 0), ty + 4 + strum, 2, 2, SKIN[0]);                       // the strumming hand
  rect(ctx, 33, 230, 1, 1, frac(t) < 0.5 ? '#ff3030' : '#5a1010');
  if (hash(5, step(t, 480), 91) < 0.3) rect(ctx, 62 + Math.floor(hash(6, step(t, 480), 92) * 16), 243, 1, 1, '#fff2a0');                 // coins in the case
  if (!playing) return;
  for (let k = 0; k < 3; k++) {                         // notes drifting up
    const { cyc, u } = cycle(t, 48 + k * 12, k * 0.37), nx = x + 14 + k * 5 + Math.round(wave(t, 30 + k * 7, k * 0.2) * 3), ny = ty - 6 - Math.round(u * 24);
    ctx.globalAlpha = Math.min(1, u * 6) * (1 - u); ctx.fillStyle = NEON[(cyc + k) % 6];
    NOTE.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '#') ctx.fillRect(nx + i, ny + j, 1, 1); });
  }
  ctx.globalAlpha = 1;
}
function drawListeners(t, bz, shower) {
  const clap = (t % 60) >= 52 && (t % 60) < 58, c2 = Math.floor(t * 8) & 1, fl = Math.floor(t * 6) & 1, ty = TOP(238, 1) + 5;
  put(ACT.fan, 88, 238);
  if (clap) { rect(ctx, 86, ty + 1, 1, 3, sleeve(STY[4])); rect(ctx, 83 + c2, ty + 3, 2, 2, SKIN[0]); rect(ctx, 82 - c2, ty + 3, 1, 2, SKIN[0]); }
  else { rect(ctx, 86, ty + 1, 2, 3, sleeve(STY[4])); rect(ctx, 85, ty, 1, 2, SKIN[0]); rect(ctx, 81 - fl, ty - 4, 3, 4, '#3a8ae0'); rect(ctx, 82 - fl, ty - 3, 1, 2, '#bfe0ff'); }
  put(ACT.bouncer, 99, 239 - [0, 1, 2, 1][Math.floor(t * 6) & 3]);
  if (shower) drawUmb(89, TOP(238, 1) - 3, 2, umbOpen(t, 220), true);
}
function drawSuit(t, bz, shower) {
  const x = 72 + Math.round(wave(t, 30, 0.1) * 0.7);
  put(ACT.suit, x, 251);
  if ((t % 60) >= 52 && (t % 60) < 58) rect(ctx, x - 1 + (Math.floor(t * 8) & 1), TOP(251, 2) + 9, 2, 2, SKIN[0]);
  if (shower) drawUmb(x, TOP(251, 2) - 3, 4, umbOpen(t, 221), true);
}
/* ---- the takoyaki stall: the cook turning his batch, the helper at the side window, steam ---- */
const STEAM = [];
{ const r = rng(61); for (let i = 0; i < 18; i++) STEAM.push({ n: 40 + Math.floor(r() * 14), ph: r(), k: 16 + Math.floor(r() * 20), p: r(), x: r() }); }
function drawStall(t, bz, shower, crowd) {
  const x0 = STALL.x, cx = 438, a = Math.floor(t * 6) & 1, top = 211 + (frac(t / 3) > 0.8 ? 1 : 0), ty = top + 5;
  halo(HL.stall, x0 + 46, 214, 0.9);
  ctx.drawImage(STALLC.back, x0, 190);
  if (crowd > 0) {                                     // the cook and the helper; at crowd 0 the stall stands lit and unattended, the griddle steaming
    ctx.drawImage(ACT.cook, cx - PCX, top - 1);
    rect(ctx, cx - 4, ty, 1, 3, '#f4f0ea'); rect(ctx, cx - 5 - a, ty + 3, 1, 2, SKIN[1]); rect(ctx, cx - 5 - a, ty + 5 + a, 1, 2, '#e8d8b0');      // two picks, flick, flick
    rect(ctx, cx + 3, ty, 1, 3, '#f4f0ea'); rect(ctx, cx + 5 - a, ty + 3, 1, 2, SKIN[1]); rect(ctx, cx + 5 - a, ty + 6 - a, 1, 2, '#e8d8b0');
    const u = t % 20, give = u > 0.6 && u < 2.4, hx = 404, hty = 216;
    ctx.drawImage(ACT.helper, hx - PCX, 211);
    if (give) {                                        // a tray goes out to the head of the queue
      rect(ctx, hx - 9, hty + 3, 8, 1, sleeve(STY[10])); rect(ctx, hx - 11, hty + 3, 2, 1, SKIN[0]);
      if (u < 1.2) { rect(ctx, hx - 16, hty + 2, 5, 2, '#ecdcb4'); rect(ctx, hx - 15, hty + 1, 1, 1, '#b8702a'); rect(ctx, hx - 13, hty + 1, 1, 1, '#b8702a'); }
    } else { const b = Math.round(wave(t, 40, 0.1) * 2); rect(ctx, hx - 2, hty + 1, 1, 3, sleeve(STY[10])); rect(ctx, hx - 3 + b, hty + 4, 1, 3, SKIN[0]); rect(ctx, hx - 4 + b, hty + 7, 2, 1, '#5a2a10'); }
  }
  ctx.drawImage(STALLC.front, x0, 190);
  for (const bx of [x0 + 24, x0 + 62]) { rect(ctx, bx, 209, 2, 2, '#fffae0'); halo(HL.bulb, bx + 1, 210, 0.9 + 0.08 * wave(t, 1300 + bx, 0.2)); }
  const sw = Math.round(wave(t, 26, 0.3) * 1.2);
  ctx.drawImage(LANT_R, x0 - 5 + sw, 206); halo(HL.lantern, x0 - 2 + sw, 211, 0.85 + 0.1 * wave(t, 1100, 0.4));
  for (const s of STEAM) {
    const q = t * s.n / LOOP + s.ph, v = q - Math.floor(q), x = x0 + 28 + s.x * 34 - v * 22 + wave(t, s.k, s.p) * 3 * v, y = 221 - v * 52 - v * v * 8, rr = Math.min(6, 1 + Math.floor(v * 7));
    ctx.globalAlpha = 0.5 * Math.min(1, v * 8) * Math.pow(1 - v, 1.4);
    ctx.drawImage(PUFFS[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}
function drawRack(t) {
  RACK_L.forEach(([x, y], k) => {
    const sw = Math.round(wave(t, 22 + (k % 3) * 5, k * 0.17) * 1.1);
    rect(ctx, x + 3, y - 3, 1, 3, '#3a2618');
    ctx.drawImage(LANT_B, x + sw, y);
    if (k % 3 === 1) halo(HL.rack, x + 3 + sw, y + 5, 0.85 + 0.1 * wave(t, 1200 + k * 90, k * 0.3) + 0.05 * wave(t, 2300, k * 0.7));
  });
}
/* ---- a delivery rider comes through once a minute ---- */
const CYC = [22, 82, 142, 202], CYC_DUR = 9;
function renderBike(f) {
  const p = pix(27, 22);
  for (const wx of [5, 20]) {
    for (let a = 0; a < 28; a++) p.set(wx + Math.cos(a / 28 * TAU) * 4.2, 17 + Math.sin(a / 28 * TAU) * 4.2, '#d0d0e0');
    p.set(wx + (f ? 2 : 0), 17 + (f ? 0 : 2), '#8a8aa0'); p.set(wx - (f ? 2 : 0), 17 - (f ? 0 : 2), '#8a8aa0'); p.set(wx, 17, '#5a5a70');
  }
  for (const [a, b, c, d] of [[5, 17, 11, 11], [11, 11, 18, 11], [18, 11, 20, 17], [11, 11, 13, 17], [13, 17, 5, 17]]) p.line(a, b, c, d, '#e8342c');
  p.line(18, 11, 19, 7, '#3a3a4a'); p.fill(18, 7, 3, 1, '#3a3a4a'); p.fill(9, 10, 4, 1, '#22222c');
  p.fill(0, 4, 8, 7, '#2ac8b8'); p.fill(0, 4, 8, 1, '#8af0e4'); glyphP(p, ['#.#', '.#.', '#.#'], 2, 6, '#0a5a54');            // the delivery box
  for (let j = 0; j < 6; j++) p.fill(10 + j, 8 - j, 3, 2, '#1a8a80');                                                         // the rider, leaning in
  p.fill(15, 0, 4, 2, '#f4f4f8'); p.fill(16, 2, 3, 2, SKIN[1]); p.line(16, 5, 19, 7, SKIN[1]);
  p.line(11, 10, f ? 14 : 12, f ? 14 : 16, '#22263a', 2); p.fill(f ? 14 : 12, f ? 16 : 18, 3, 1, '#16121a');
  p.fill(22, 9, 2, 2, '#fff6d0');
  return p.done();
}
function drawCyclist(t) {
  const tau = (t - 22 + LOOP) % 60;
  if (tau >= CYC_DUR) return;
  const x = Math.round(lerp(-30, W + 6, tau / CYC_DUR));
  ctx.drawImage(BIKE[Math.floor(t * 10) & 1], x, 259 - 21);
  halo(HL.bike, x + 24, 259 - 11, 0.9);
}

/* ---- goldfish scooping: a low blue tank, two children dipping paper scoops, grandpa fanning himself, a mother watching ---- */
const TANK = { x: 276, w: 38, y: 250, fy: 258 };
function drawGoldfish(t, bz, shower, crowd) {
  const { x, w, y } = TANK, dip = Math.floor(t * 2) & 1, mum = RANK.scoopMum < crowd;   // grandpa keeps the tank; the mother and the two children thin
  const keeper = crowd > 0;                            // at crowd 0 he is gone too: the tank, his empty stool and the lantern stay
  if (keeper) ctx.drawImage(ACT.gran, x - 10 - PCX, TANK.fy - 20);
  rect(ctx, x - 13, 253, 7, 2, '#5a4030'); rect(ctx, x - 12, 255, 1, 3, '#3a2a20'); rect(ctx, x - 8, 255, 1, 3, '#3a2a20');   // on his stool
  if (keeper) {
    const gy = TANK.fy - 20 + 6, fl = Math.floor(t * 4) & 1;
    armDown(x - 14, gy, STY[18], 7); rect(ctx, x - 7, gy, 1, 3, sleeve(STY[18])); rect(ctx, x - 6, gy - 1, 1, 3, SKIN[1]); rect(ctx, x - 6 + fl, gy - 5, 4, 4, '#f4f0e4'); rect(ctx, x - 5 + fl, gy - 4, 2, 2, '#e8342c');
  }
  if (mum) { put(ACT.mother, x + 33, 250); armDown(x + 29, TOP(250, 1) + 5, STY[17], 8); armDown(x + 36, TOP(250, 1) + 5, STY[17], 8); }
  [[x + 10, ACT.kidF[0], 0, 15], [x + 22, ACT.kidF[1], 1, 16]].forEach(([kx, spr, k, si]) => {
    if (k ? !mum : RANK.scoopKid >= crowd) return;       // the child beside the mother goes with her, the other one before them
    ctx.drawImage(spr, kx - PCX, 241);
    const d = (dip + k) & 1;                             // one arm down to the water with the scoop
    rect(ctx, kx + 3, 246, 1, 2 + d * 2, SKIN[STY[si].sk]); rect(ctx, kx + 2, 248 + d * 2, 3, 1, '#ffffff'); rect(ctx, kx - 4, 246, 1, 3, SKIN[STY[si].sk]);
  });
  rect(ctx, x, y, w, 8, '#2a78c8'); rect(ctx, x, y, w, 1, '#8ad8ff'); rect(ctx, x, y + 7, w, 1, '#1a4a8a'); rect(ctx, x, y + 1, 1, 6, '#5ab0e8'); rect(ctx, x + w - 1, y + 1, 1, 6, '#1a4a8a');
  for (let k = 0; k < 9; k++) {                          // the fish
    const fx = x + 3 + Math.floor(hash(k, 9, 1) * (w - 9)) + Math.round(wave(t, 14 + k * 3, k * 0.21) * 3), fyy = y + 2 + (k % 4) + Math.round(wave(t, 22 + k * 5, k * 0.4) * 0.6);
    rect(ctx, fx, fyy, 2, 1, ['#ff7a20', '#ff3a2a', '#fff0e0', '#ff9a40', '#1a1a22'][k % 5]);
  }
  ctx.globalAlpha = 0.5; rect(ctx, x + 2 + ((step(t, 480) * 5) % (w - 8)), y + 1, 4, 1, '#d8f4ff'); ctx.globalAlpha = 1;
  rect(ctx, x + w + 4, 232, 1, 26, '#5a3a24'); ctx.drawImage(LANT_B, x + w + 1 + Math.round(wave(t, 24, 0.2) * 0.8), 226); halo(HL.lantern, x + w + 4, 231, 0.9 + 0.08 * wave(t, 1000, 0.3));
  if (shower) { if (mum) drawUmb(x + 33, TOP(250, 1) - 3, 7, umbOpen(t, 230), true); if (keeper) drawUmb(x - 10, TANK.fy - 23, 2, umbOpen(t, 231), true); }
}

function buildPeople() {
  TINY = STY.slice(0, 13).map(st => [0, 1].map(f => { const c = tiny(st, f); return [c, flipped(c)]; }));
  for (const w of WK) w.spr = [0, 1, 2, 3].map(f => { const c = person(STY[w.st], w.S, 'side', f); return w.dir > 0 ? c : flipped(c); });
  QSPR = QS.map(si => { const r = [0, 1, 2, 3].map(f => person(STY[si], 1, 'side', f)); return { r, l: r.map(flipped) }; });
  ACT = {
    photo: person(STY[9], 0, 'back', 0, false), her: person(STY[12], 0, 'back', 0, false), him: person(STY[5], 1, 'back', 0, false),
    teenA: person(STY[6], 0, 'back', 0, false), teenB: person(STY[8], 0, 'back'),
    mum: person(STY[7], 1, 'side', 1), kid: [0, 1, 2, 3].map(f => person(STY[15], 'k', 'side', f)),
    sitA: person(STY[0], 1, 'sit', 0, false), sitB: person(STY[11], 1, 'sit', 0, false),
    busker: person(STY[14], 2, 'side', 1, false), fan: flipped(person(STY[4], 1, 'side', 1, false)), bouncer: flipped(person(STY[16], 'k', 'side', 1)),
    suit: person(STY[2], 2, 'back'), cook: person(STY[13], 1, 'front', 0, false), helper: flipped(person(STY[10], 1, 'side', 1, false)),
  };
  ACT.kidL = ACT.kid.map(flipped);
  ACT.dad = person(STY[0], 1, 'back', 0, false); ACT.son = person(STY[16], 'k', 'back', 0, false); ACT.pairA = person(STY[1], 0, 'back'); ACT.pairB = person(STY[20], 0, 'back', 0, false); ACT.trav = person(STY[19], 1, 'back', 0, false);
  ACT.gran = person(STY[18], 1, 'sit', 0, false); ACT.mother = person(STY[17], 1, 'front', 0, false); ACT.kidF = [person(STY[15], 'k', 'front', 0, false), person(STY[16], 'k', 'front', 0, false)];
  DOG = DOG_ROWS.map(rows => { const p = pix(9, 5); p.rows(rows, 0, 0, { e: '#5a3214', d: '#d89a50', t: '#f4e8d0' }); const c = p.done(); return [c, flipped(c)]; });
  BIKE = [renderBike(0), renderBike(1)];
  // back to front: everything standing on the boards, sorted once by its feet; an entry with a rank is one unit of
  // the crowd and is skipped while its rank is not below the crowd share (the others thin inside, or not at all).
  // Rank 0 is the essential cast: there at every share above 0, gone at crowd 0 with everything that is theirs. The
  // stall, the goldfish tank and the cat stay at 0 and leave out their people themselves.
  CAST = [
    { fy: 209, fn: drawRack }, { fy: 213.5, fn: drawRailPeople }, { fy: 221.5, rank: 0, fn: drawKid }, { fy: BENCH.feet, rank: 0, fn: drawBench }, { fy: 232.5, fn: drawAlleyCat },
    { fy: 238, rank: 0, fn: drawBusker }, { fy: 238.5, rank: RANK.fans, fn: drawListeners }, { fy: 251, rank: RANK.suit, fn: drawSuit }, { fy: STALL.feet, fn: drawStall }, { fy: 239, rank: 0, fn: drawQueue },
    { fy: 259, rank: 0, fn: drawCyclist }, { fy: TANK.fy, fn: drawGoldfish }, ...WK.map(w => ({ fy: w.fy, rank: w.rank, fn: (t, bz, sh) => drawWalker(w, t, sh) })),
  ].sort((a, b) => a.fy - b.fy);
}

/* ================= Fireworks far down the canal (two testers first, then the show) ================= */
const FWC = [['#fff2c0', '#ffd040', '#ff8a20'], ['#ffe0f0', '#ff5ab0', '#c0208a'], ['#e0fff0', '#5affb0', '#20a870'], ['#e0f4ff', '#5ac8ff', '#2a6ae0'],
  ['#fff0e0', '#ff6a4a', '#c02a2a'], ['#f0e0ff', '#b07aff', '#6a3ad0'], ['#fffbe0', '#ffe9a0', '#d8a040']];
const FWS = [
  { t: 82.5, x: 104, y: 60, r: 9, c: 2, small: 1 }, { t: 87.2, x: 126, y: 54, r: 10, c: 0, small: 1 },
  { t: 92, x: 110, y: 42, r: 31, c: 0 }, { t: 93.9, x: 80, y: 36, r: 25, c: 3 }, { t: 95.6, x: 140, y: 38, r: 28, c: 1 },
  { t: 97.2, x: 104, y: 32, r: 35, c: 4 }, { t: 98.8, x: 150, y: 30, r: 24, c: 2 }, { t: 100.4, x: 74, y: 44, r: 22, c: 5 },
  { t: 101.6, x: 122, y: 36, r: 35, c: 1 }, { t: 103, x: 90, y: 30, r: 28, c: 3 }, { t: 104.2, x: 144, y: 34, r: 31, c: 0 },
  { t: 105.4, x: 112, y: 32, r: 40, c: 6, willow: 1 },
];
function drawFireworks(t) {
  if (t < FIRE.build || t > FIRE.b + 2) return;
  for (let k = 0; k < FWS.length; k++) {
    const F = FWS[k], dt = t - F.t, life = F.willow ? 4.2 : 2.6;
    if (dt < -1.2 || dt > life) continue;
    if (dt < 0) {                                      // the shell climbing
      const a = 1 + dt / 1.2, y = Math.round(lerp(122, F.y, 1 - (1 - a) * (1 - a)));
      rect(ctx, F.x + Math.round(Math.sin(a * 9)), y, 1, 2, '#ffe9b0'); ctx.globalAlpha = 0.4; rect(ctx, F.x, y + 2, 1, 3, '#ffb060'); ctx.globalAlpha = 1;
      continue;
    }
    const u = dt / life, e = 1 - Math.pow(1 - Math.min(1, u * 1.5), 3), pal = FWC[F.c], n = F.small ? 14 : F.willow ? 56 : 44, tw = step(t, 2400);
    const g = Math.max(0, 1 - u * 2.2);
    if (g > 0) { halo(HL.fw[F.c], F.x, F.y, g * (F.small ? 0.5 : 1)); if (!F.small) halo(HL.sheet, F.x, F.y + 20, g * 0.22); }
    for (let i = 0; i < n; i++) {
      const ang = TAU * (i + hash(i, k, 3) * 0.7) / n, sp = i % 3 === 0 ? 0.5 + 0.2 * hash(i, k, 4) : 0.8 + 0.2 * hash(i, k, 4), drop = u * u * (F.willow ? 26 : 10);
      if (u > 0.55 && hash(i, tw, k) < 0.4) continue;                                             // dying sparks twinkle
      const ca = Math.cos(ang), sa = Math.sin(ang) * 0.94, len = F.small ? 2 : F.willow ? 6 : 4;
      for (let j = 0; j < len; j++) {                  // each spark drags a short tail back towards the centre
        const rr = F.r * e * sp * (1 - j * (F.willow ? 0.07 : 0.1) * (1 - u * 0.6));
        ctx.globalAlpha = Math.min(1, (1 - u) * 1.7) * (1 - j / len) * (F.willow && u > 0.3 ? 0.8 : 1);
        ctx.fillStyle = j === 0 && u < 0.5 ? pal[0] : pal[u < 0.6 ? 1 : 2];
        ctx.fillRect(Math.round(F.x + ca * rr), Math.round(F.y + sa * rr + drop * (1 - j * 0.12)), j === 0 && u < 0.4 && !F.small ? 2 : 1, 1);
      }
    }
    ctx.globalAlpha = 1;
    if (u < 0.08) rect(ctx, F.x - 1, F.y - 1, 3, 3, '#ffffff');
  }
}

/* ================= The evening shower (the city's own weather) and the picked weather ================= */
// Osaka's own falling kind is a short warm shower late in the loop: heat lightning, first drops, umbrellas popping
// open across the bridge, wet boards, then it eases off. A picked weather (site/js/engine/weather.js) falls all loop long.
const WEATHER = weatherPlans(['shower']);
const WX_LAND = [[128, 160], [170, 206], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: HERO.x + 27, y: 72, rx: 40, ry: 62, col: '#ffb080' }, { x: 293, y: 74, rx: 30, ry: 54, col: '#a0e0ff' }, { x: 355, y: 76, rx: 28, ry: 54, col: '#ffe0b0' },
  { x: 402, y: 76, rx: 32, ry: 54, col: '#ffe890' }, { x: 456, y: 76, rx: 30, ry: 56, col: '#ff90b0' }, { x: 436, y: 214, rx: 52, ry: 28, col: '#ffcf90' },
  { x: 16, y: 172, rx: 30, ry: 36, col: '#ffd8a0' }, { x: WHEEL.cx, y: 80, rx: 24, ry: 46, col: '#ffe080' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS }, WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 25, y: BB.y + 6, rx: 32, ry: 16, col: '#ffe8c8' }] };
const RAIN = [
  { count: 200, nMin: 150, nMax: 180, len: 3, slope: 0.1, col: 'rgba(176,182,236,0.4)', land: [128, 162] },
  { count: 170, nMin: 190, nMax: 220, len: 5, slope: 0.14, col: 'rgba(196,202,244,0.5)', land: [170, 206] },
  { count: 100, nMin: 240, nMax: 270, len: 7, slope: 0.2, col: 'rgba(220,226,252,0.6)', land: [214, 268] },
];
RAIN.forEach((L, li) => { const r = rng(100 + li); L.drops = []; for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() }); });
const showerI = t => (t < SHOWER.a || t >= SHOWER.b ? 0 : Math.min(1, (t - SHOWER.a) / (SHOWER.full - SHOWER.a), (SHOWER.b - t) / (SHOWER.b - SHOWER.ease)));
function drawRain(L, t, I) {
  ctx.fillStyle = L.col;
  for (let i = 0, n = Math.round(L.drops.length * I); i < n; i++) {
    const d = L.drops[i], { cyc, u } = cycle(t, d.n, d.ph);
    const yl = lerp(L.land[0], L.land[1], hash(d.id, cyc, 12)), xl = -10 + hash(d.id, cyc, 11) * (W + 40);
    if (u < 0.86) {
      const yh = yl - (yl + 10) * (1 - u / 0.86), xh = xl - (yl - yh) * L.slope;
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh - k * L.slope), Math.round(yh - k), 1, 1);
    } else if (L.len > 2) {
      const s = (u - 0.86) / 0.14, Y = Math.round(yl - 1 - Math.sin(Math.PI * s) * 2);
      ctx.globalAlpha = 1 - s; ctx.fillRect(Math.round(xl - 1 - s * 2), Y, 1, 1); ctx.fillRect(Math.round(xl + 1 + s * 2), Y, 1, 1); ctx.globalAlpha = 1;
    }
  }
}
const RINGS = [];
{ const r = rng(71); for (let i = 0; i < 26; i++) RINGS.push({ id: i, n: 100 + Math.floor(r() * 40), ph: r() }); }
function drawRings(t, I) {
  ctx.fillStyle = '#9aa4e0';
  for (let i = 0, n = Math.round(RINGS.length * I); i < n; i++) {
    const g = RINGS[i], { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.5) continue;
    const s = u / 0.5, x = hash(g.id, cyc, 1) * W, y = 172 + hash(g.id, cyc, 2) * 30, rr = 1 + s * 4, pts = 6 + Math.floor(rr * 2);
    ctx.globalAlpha = (1 - s) * 0.5;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * rr * 0.34), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
function drawRipples(t) {
  for (let k = 0; k < 44; k++) {                       // dark and bright dashes drifting across the mirror
    const { cyc, u } = cycle(t, 20 + (k % 7) * 3, k * 0.137), y = WATER + 3 + Math.floor(hash(k, cyc, 1) * 38);
    ctx.globalAlpha = 0.6 * Math.sin(Math.PI * u);
    rect(ctx, Math.floor(hash(k, cyc, 2) * W) + Math.round(u * 10), y, 4 + Math.floor(hash(k, cyc, 3) * 10), 1, k % 3 ? '#03040c' : '#3a4678');
  }
  const s = step(t, 960);
  for (let k = 0; k < 14; k++) {                       // lights trembling on the far water, under the bridge
    const x = 10 + Math.floor(hash(k, 5, 1) * 132), q = x < VP.x ? quayN(x) : quayS(x);
    if (hash(k, s, 2) < 0.5) { ctx.globalAlpha = 0.8; rect(ctx, x, q + 1 + Math.floor(hash(k, 5, 3) * Math.max(1, WATER - q - 1)), 2, 1, k & 1 ? '#ffb0d0' : '#ffd8a0'); }
  }
  UNDER.signs.forEach((g, k) => {                     // and the signs over it
    const q = g.x < VP.x ? quayN(g.x) : quayS(g.x), room = WATER - q - 1;
    if (room >= 1 && hash(k, s, 4) < 0.5) { ctx.globalAlpha = 0.7; rect(ctx, g.x - (hash(k, s, 5) < 0.5 ? 1 : 0), q + 1 + Math.floor(hash(k, s, 6) * room), Math.min(3, g.w + 1), 1, g.col); }
  });
  ctx.globalAlpha = 1;
}
function sheetI(t, reducedMotion) {                    // heat lightning flickering inside the clouds
  if (reducedMotion) return 0;
  for (const s of SHEETS) { const dt = t - s; if (dt >= 0 && dt < 0.7) return (1 - dt / 0.7) * (0.45 + 0.55 * hash(7, Math.floor(t * 20))); }
  return 0;
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR, CAN, CAN_BARE, BBF_C, BBAD, PITCH, WALL, BURST, OCTO, CAPTION, STRIPES_C, RAMEN_C, GLYPHS_C, CATB_C, BAN_C, COL_C,
  WAVEB, PUFFER_C, TEAL_C, GYOZA, MUG_C, DARUMA_C, RAMB_C, PUFFS, UNDER, BRIDGE_C, LOWER, TINY, TOUR, TOUR_SPR, TOUR_BARE, DRUM_SPR, BOARD_C, STALLC, LANT_B, LANT_S, LANT_R,
  ACT, QSPR, DOG, BIKE, CAST, SHADOW_C, VIG_C, STREAKS, STREAKS_BB;
function renderShadow() {                              // the bridge's shadow on the water under and beside it
  const p = pix(190, BOARD + 2 - WATER);
  for (let y = 0; y < p.h; y++) for (let x = 0; x < 190; x++) {
    const v = clamp((170 - x) / 60, 0, 1) * clamp(1.15 - y / 46, 0, 1) * 0.62, q = Math.floor(v * 5 + bayer(x, y)) / 5;
    if (q > 0) p.set(x, y, [4, 5, 16], Math.round(Math.min(1, q) * 255));
  }
  return p.done();
}
function renderVignette() {
  const p = pix(W, H), c = [6, 3, 18];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}
const vflip = c => { const [f, x] = makeCanvas(c.width, c.height); x.translate(0, c.height); x.scale(1, -1); x.drawImage(c, 0, 0); return f; };
const boatSet = c => { const l = flipped(c); return [c, l, vflip(c), vflip(l)]; };
function prerender() {
  HL = {
    moon: makeHalo(26, '#b8a8ff', 0.2, 1.8), sheet: makeHalo(110, '#c8b8ff', 0.4, 1.3), wheel: makeHalo(34, '#ffc840', 0.16, 1.6), red: makeHalo(5, '#ff3030', 0.6, 1),
    neon: NEON.map(c => makeHalo(9, c, 0.3)), bbLamp: makeHalo(6, '#ffe8c8', 0.5), bbGlow: makeHalo(30, '#ff9a70', 0.1),
    hero: makeHalo(60, '#ff8a50', 0.16, 1.5), cyan: makeHalo(30, '#5ee8ff', 0.16), crim: makeHalo(28, '#ff4a5a', 0.16), puffer: makeHalo(22, '#ffe0a0', 0.3),
    warm: makeHalo(24, '#ffb060', 0.2), pink: makeHalo(24, '#ff5ab4', 0.2), vend: makeHalo(12, '#c0e4ff', 0.3), lamp: makeHalo(14, '#ffe0a8', 0.35),
    flash: makeHalo(8, '#ffffff', 0.8, 1.2), lanterns: makeHalo(20, '#ffcf80', 0.2), boat: makeHalo(30, '#ffe090', 0.12), festival: makeHalo(28, '#ff9a50', 0.35),
    lantern: makeHalo(11, '#ff8a50', 0.35), stall: makeHalo(50, '#ffb060', 0.2, 1.5), bulb: makeHalo(12, '#fff0c0', 0.4), rack: makeHalo(24, '#ffc878', 0.28),
    fw: FWC.map(p => makeHalo(42, p[1], 0.3, 1.4)), bike: makeHalo(8, '#fff0c8', 0.5),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer); FAR = renderFar();
  CAN = renderCanyon(true); CAN_BARE = renderCanyon(false); BBF_C = renderBBFrame(); BBAD = renderBBAd(); PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  WALL = renderWall();
  BURST = BURSTS.map((pal, k) => [0, 1, 2, 3].map(ph => renderBurst(pal, ph, k === 3))); OCTO = [0, 1, 2, 3].map(renderOcto); CAPTION = [renderCaption(false), renderCaption(true)];
  STRIPES_C = renderStripes(); RAMEN_C = [0, 1, 2].map(renderRamen); GLYPHS_C = [renderBigGlyphs(false), renderBigGlyphs(true)]; CATB_C = renderCatBoard();
  BAN_C = [renderBanner(false), renderBanner(true)]; COL_C = [renderColumn(false), renderColumn(true)];
  WAVEB = renderWaveBoard(); PUFFER_C = renderPuffer(); TEAL_C = renderTeal(); GYOZA = renderGyoza(); MUG_C = renderMug(); DARUMA_C = renderDaruma();
  RAMB_C = [renderRamenBoard(false), renderRamenBoard(true)];
  PUFFS = [null]; for (let r = 1; r <= 6; r++) PUFFS.push(makePuff(r, '#f6eef4', '#cac2d6'));
  UNDER = renderUnder(); BRIDGE_C = renderBridge(); LOWER = renderLower();
  TOUR = renderTourBoat(); TOUR_SPR = [...TOUR_CUTS.map(cut => boatSet(renderTourBoat(cut).c)), boatSet(TOUR.c)]; DRUM_SPR = boatSet(renderDrumBoat());
  TOUR_BARE = boatSet(renderTourBoat(0, false).c);    // crowd 0: no passengers, no guide
  BOARD_C = renderBoards(); STALLC = renderStall(); LANT_B = renderLantern(true); LANT_S = renderLantern(false);
  { const p = pix(7, 11); for (let y = 1; y < 10; y++) { const hw = y === 1 || y === 9 ? 2 : 3; for (let i = 3 - hw; i <= 3 + hw; i++) p.set(i, y, y === 5 ? '#fff0d0' : pick(['#a81c1c', '#e0382a', '#ff7040'], 1 - Math.abs(i - 3) / 3.6, i, y)); } p.fill(2, 0, 3, 1, '#1a0c0c'); p.fill(2, 10, 3, 1, '#1a0c0c'); LANT_R = p.done(); }
  buildPeople();
  SHADOW_C = renderShadow(); VIG_C = renderVignette();
  STREAKS = [
    { x: HERO.x + 27, w: 10, col: '#ff7a50', a: 0.4, seed: 1 }, { x: LED.x + 23, w: 8, col: '#5ee8ff', a: 0.32, seed: 2 }, { x: PUF.x, w: 6, col: '#ffe0a0', a: 0.34, seed: 3 },
    { x: GYO.x + 26, w: 8, col: '#ffe060', a: 0.3, seed: 4 }, { x: COLN.x + 6, w: 4, col: '#ff5a8a', a: 0.34, seed: 5 }, { x: DAR.x, w: 5, col: '#ff4a3a', a: 0.3, seed: 6 },
    { x: 330, w: 4, col: '#ff7ab0', a: 0.28, seed: 7 }, { x: 310, w: 5, col: '#ffcf80', a: 0.24, seed: 8 }, { x: 388, w: 5, col: '#ffcf80', a: 0.24, seed: 9 },
    { x: 408, w: 6, col: '#ffb050', a: 0.3, seed: 10 }, { x: 449, w: 5, col: '#e050c0', a: 0.26, seed: 11 }, { x: WHEEL.cx, w: 4, col: '#ffc840', a: 0.26, seed: 12 },
    { x: 126, w: 3, col: '#ffe0a8', a: 0.2, seed: 13 }, { x: 74, w: 3, col: '#ffe0a8', a: 0.2, seed: 14 },
  ];
  STREAKS_BB = [...STREAKS, { x: BB.x + 25, w: 6, col: '#ff7a5a', a: 0.22, seed: 15 }];
}

/* ================= Frame compositor (back to front) ================= */
/** render(t) paints the city's own billboard ad; { pitch } paints one of the site's faces on it; { billboard: false }
 *  leaves the sign out (the loop videos); { weather } draws a WEATHERS pick, the city's own when left out;
 *  { crowd } is the share of the crowd to show, 0..1 (CROWD when left out or not a number); exactly 0 draws nobody */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  const crowd = Number.isFinite(share) ? clamp(share, 0, 1) : CROWD;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, rainI = wx.shower ? showerI(t) : 0, shower = !!wx.shower && t > SHOWER.a && t < SHOWER.b + 1;
  const bz = buzz(t), L = wallLight(t);
  drawSky(t, wx.shower ? sheetI(t, reducedMotion) : 0);
  drawFireworks(t);
  blit(FAR.c); drawWins(FAR.wins, t, 7);
  if (rainI > 0) drawRain(RAIN[0], t, rainI);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawCanyon(t, pitch, billboard);
  drawUnder(t);
  drawBridgeCrowd(t, bz, shower, crowd);
  drawSigns(t);
  drawFarBank(t, shower, crowd);
  // the canal: black water doubling the far bank and the signs, streaks under the brightest lights, the boats
  rect(ctx, 0, WATER, W, BOARD + 2 - WATER, '#050816');
  reflect(ctx, t, { top: WATER, rows: BOARD + 2 - WATER, squash: 3, alpha: 0.78, k1: 40, k2: 96, amp: 0.9, grow: 0.07 });
  ctx.drawImage(SHADOW_C, 0, WATER);
  for (const s of billboard ? STREAKS_BB : STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a * Math.min(1.2, L), s.seed, WATER + 2, BOARD + 2);
  drawRipples(t);
  if (rainI > 0) drawRings(t, rainI);
  drawBoats(t, crowd);
  if (rainI > 0) drawRain(RAIN[1], t, rainI);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the near boardwalk and everyone on it
  ctx.drawImage(BOARD_C.c, 0, 0);
  if (crowd === 0) ctx.drawImage(BOARD_C.bare, KIT.x, KIT.y);                                   // nobody: the busker's amp and case are gone too
  reflect(ctx, t, { top: BOARD + 2, rows: 46, squash: 1.3, alpha: 0.13 + (wx.shower ? 0.16 * env(t, SHOWER.a + 3, 239, 5) : 0), amp: 0.4, k1: 30, k2: 70 });   // the boards shine a little, more when wet
  for (const c of CAST) if (!(c.rank >= crowd)) c.fn(t, bz, shower, crowd);
  if (rainI > 0) drawRain(RAIN[2], t, rainI);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'osaka',
  name: 'Osaka',
  tagline: 'Lantern night on Dotonbori',
  timeZone: 'Asia/Tokyo',
  accent: '#ff1fe6',                                   // = ACCENT, written out: tools/seo/seo.mjs reads it from the source
  focusX: HERO.x + 27,
  landmark: [HERO.x - 2, HERO.x + HERO.w + 2],         // the giant octopus board at the bridge's end
  landmarkTop: HERO.y - 4,                             // the roof line over the board, which landscape crops keep
  billboard: BB,
  crowd: CROWD,                                        // the share of the crowd shown by default; render(t, { crowd }) takes any 0..1
  ambience: { crowd: 0.45, water: 0.3, wind: 0.05 },
  events: [
    ...TOURS.map(b => ({ t: b.a, type: 'boat', dur: b.b - b.a, pan: b.dir > 0 ? [-0.8, 0.9] : [0.9, -0.8], gain: 0.3 })),
    { t: SHOW.on, type: 'chime', x: 340 },
    ...FWS.map(f => ({ t: +(f.t + 0.3).toFixed(1), type: 'firework', x: f.x, gain: f.small ? 0.25 : 0.5 })),
    { t: DONDOKO.a - 6, type: 'boat', dur: DONDOKO.b - DONDOKO.a + 6, pan: [-0.9, 0.9], gain: 0.4 },
    ...SHEETS.map(s => ({ t: +(s + 1.2).toFixed(1), type: 'thunder', x: 300, gain: 0.3 })),
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
